// src/services/funcionService.js
import authService from './authService';
import { makeKey, getCached, setCached, isFresh, touch, singleFlight } from './cacheService';

// -------------------------
// Helpers
// -------------------------

// Manejo de respuestas (JSON / texto y 204)
const handleResponse = async (response) => {
  const status = response?.status ?? 0;

  if (!(response instanceof Response)) {
    return response ?? null;
  }

  if (status === 204) return null;

  const ct = response.headers.get("content-type") || "";
  let data = null;

  try {
    if (ct.includes("application/json")) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = text || null;
    }
  } catch {
    data = null;
  }

  if (!response.ok) {
    const msg =
      data?.message ||
      data?.error ||
      data?.details ||
      `Error en la petición. ${status}`;

    const err = new Error(msg);
    err.status = status;
    err.code = data?.code || null;
    err.response = data;
    throw err;
  }

  return data;
};

// Wrapper local que usa el de auth y parsea con handleResponse
const authenticatedRequest = async (path, options = {}) => {
  const res = await authService.authenticatedRequest(path, options);
  return handleResponse(res);
};

export const funcionService = {
  // === FUNCIONES EXISTENTES ===

  // Admin: Obtener todos los cursos
  async fetchCourses({ page = 1, limit = 20, showAll = true, facultad_id } = {}) {
    const params = new URLSearchParams({
      page,
      limit,
      showAll
    });

    if (facultad_id != null && facultad_id !== "") {
      params.set("facultad_id", String(facultad_id));
    }

    return authenticatedRequest(`/api/courses?${params.toString()}`);
  },

  async fetchFacultades({ showAll = true, assignedOnly = false } = {}) {
    const params = new URLSearchParams({ showAll });
    if (assignedOnly) params.set("assignedOnly", "true");
    return authenticatedRequest(`/api/facultades?${params.toString()}`);
  },

  // POST /api/facultades
  async createFacultad(payload) {
    return authenticatedRequest("/api/facultades", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  // PUT /api/facultades/:id
  async updateFacultad(id, payload) {
    return authenticatedRequest(`/api/facultades/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload)
    });
  },

 // DELETE /api/facultades/:id  (force=true también elimina los cursos asociados)
  async deleteFacultad(id, { force = false } = {}) {
    const qs = force ? "?force=true" : "";
    return authenticatedRequest(`/api/facultades/${id}${qs}`, {
      method: "DELETE"
    });
  },
  // Estudiante: Obtener cursos inscritos
  async fetchStudentCourses({ page = 1, limit = 20, search = '' } = {}, { ttlMs = 120_000 } = {}) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', String(search));

    const url = `/api/courses/student?${params.toString()}`;
    // Si tienes user en memoria, úsalo; si no, 'me' para particionar cache por usuario actual
    const userId = (authService.getCurrentUser && authService.getCurrentUser()?.id) ?? 'me';

    const cacheKey = makeKey({ url, userId });
    const cached = getCached(cacheKey);

    if (isFresh(cached)) return cached.data;

    return singleFlight(cacheKey, async () => {
      const extraHeaders = {};
      if (cached?.etag) extraHeaders['If-None-Match'] = cached.etag;

      const res = await authService.authenticatedRequest(url, { headers: extraHeaders });

      if (res.status === 304 && cached?.data) {
        // Nada cambió: renovar timestamp y servir desde cache
        touch(cacheKey);
        return cached.data;
      }

      if (res.status === 404) {
        const data = { items: [] };
        setCached(cacheKey, { data, etag: res.headers.get('ETag'), ttlMs });
        return data;
      }

      const data = await handleResponse(res);
      setCached(cacheKey, { data, etag: res.headers.get('ETag'), ttlMs });
      return data;
    });
  },

  // Docente: Obtener cursos asignados
  async fetchTeacherCourses({ page = 1, limit = 20, search = '' }) {
    const params = new URLSearchParams({ page, limit, search });
    return authenticatedRequest(`/api/courses/teacher?${params.toString()}`);
  },

  // Obtener curso específico
  async getCourse(id, { ttlMs = 120_000 } = {}) {
    const url = `/api/courses/${id}`;
    const userId =
      (authService.getCurrentUser && authService.getCurrentUser()?.id) ?? "me";

    const cacheKey = makeKey({ url, userId });
    const cached = getCached(cacheKey);

    if (isFresh(cached)) return cached.data;

    return singleFlight(cacheKey, async () => {
      const extraHeaders = {};
      if (cached?.etag) extraHeaders["If-None-Match"] = cached.etag;

      const res = await authService.authenticatedRequest(url, {
        headers: extraHeaders,
      });

      if (res.status === 304 && cached?.data) {
        touch(cacheKey);
        return cached.data;
      }

      const data = await handleResponse(res);
      setCached(cacheKey, {
        data,
        etag: res.headers.get("ETag"),
        ttlMs,
      });
      return data;
    });
  },

  // Crear curso
  async createCourse(courseData) {
    return authenticatedRequest(`/api/courses`, {
      method: 'POST',
      body: JSON.stringify(courseData),
    });
  },
  // Obtener permisos por rol de un curso
  async getCourseRolePermissions(courseId) {
    return authenticatedRequest(
      `/api/courses/${courseId}/role-permissions`,
      {
        method: "GET",
      }
    );
  },

  async getMyCoursePermissions(courseId) {
    return authenticatedRequest(`/api/courses/${courseId}/my-permissions`, {
      method: "GET",
    });
  },

  // Actualizar permisos por rol de un curso
  async updateCourseRolePermissions(courseId, payload) {
    return authenticatedRequest(
      `/api/courses/${courseId}/role-permissions`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
  },



  // Actualizar curso
  async updateCourse(id, courseData) {
    return authenticatedRequest(`/api/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(courseData),
    });
  },

  // Eliminar curso
  async deleteCourse(id) {
    await authService.authenticatedRequest(`/api/courses/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  // Obtener estudiantes de un curso
  async getCourseStudents(courseId, { page = 1, limit = 20 }) {
    const params = new URLSearchParams({ page, limit });
    return authenticatedRequest(`/api/courses/${courseId}/students?${params.toString()}`);
  },

  // Asignar docente a curso
  // Asignar rol en curso (docente / asistente)
  async assignTeacherToCourse(courseId, teacherData) {
    const payload = {
      usuario_id: teacherData.usuario_id,
      rol: teacherData.rol || 'docente',
      activo: typeof teacherData.activo === 'boolean' ? teacherData.activo : true,
    };

    return authenticatedRequest(`/api/courses/${courseId}/assign-teacher`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },


  // Obtener niveles de un curso
  async getCourseLevels(courseId) {
    return authenticatedRequest(`/api/courses/${courseId}/levels`);
  },

  // === FUNCIONES PARA ADMIN DASHBOARD ===

  async fetchDashboardStats() {
    return authenticatedRequest(`/api/admin/dashboard/stats`);
  },

  async fetchUsers({ page = 1, limit = 20, search = '', role = '', status = '', facultad_id } = {}) {
    const params = new URLSearchParams({
      page, limit,
      ...(search && { search }),
      ...(role && { role }),
      ...(status && { status }),
    });

    if (facultad_id != null && facultad_id !== "") {
      params.set("facultad_id", String(facultad_id));
    }

    return authenticatedRequest(`/api/admin/users?${params.toString()}`);
  },

  async fetchStudents({ page = 1, limit = 20, search = '', course = '', includeEnrollments = true, facultad_id } = {}) {
    const params = new URLSearchParams({
      page, limit,
      ...(search && { search }),
      ...(course && { course }),
      ...(includeEnrollments && { include_enrollments: 'true' }),
    });

    if (facultad_id != null && facultad_id !== "") {
      params.set("facultad_id", String(facultad_id));
    }

    return authenticatedRequest(`/api/admin/students?${params.toString()}`);
  },

  async fetchCourseStats() {
    return authenticatedRequest(`/api/admin/courses/stats`);
  },

  async fetchRecentActivity({ page = 1, limit = 10, type = '' }) {
    const params = new URLSearchParams({ page, limit, ...(type && { type }) });
    return authenticatedRequest(`/api/admin/activity?${params.toString()}`);
  },

  async fetchRecentRegistrations({ days = 7, limit = 50 }) {
    const params = new URLSearchParams({ days, limit });
    return authenticatedRequest(`/api/admin/registrations/recent?${params.toString()}`);
  },

  // === GESTIÓN DE USUARIOS ===

  async createUser(userData) {
    try {
      const data = await authenticatedRequest(`/api/admin/users`, {
        method: "POST",
        body: JSON.stringify(userData),
        dedupe: false,
      });

      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateUser(id, userData) {
    return authenticatedRequest(`/api/admin/users/${id}`, {
      method: 'PUT',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });
  },

  async deleteUser(id) {
    return authenticatedRequest(`/api/admin/users/${id}`, { method: 'DELETE' });
  },

  async changeUserRole(userId, roleData) {
    return authenticatedRequest(`/api/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    });
  },

  async toggleUserStatus(userId, status) {
    return authenticatedRequest(`/api/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ active: status }),
    });
  },

  async getUser(id) {
    return authenticatedRequest(`/api/admin/users/${id}`);
  },

  // === REPORTES Y ANÁLISIS ===

  async fetchSystemReports({ page = 1, limit = 20, type = '', dateFrom = '', dateTo = '' }) {
    const params = new URLSearchParams({
      page, limit,
      ...(type && { type }),
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
    });
    return authenticatedRequest(`/api/admin/reports?${params.toString()}`);
  },

  async generateReport(reportType, params = {}) {
    return authenticatedRequest(`/api/reports/generate`, {
      method: 'POST',
      body: JSON.stringify({ type: reportType, parameters: params }),
    });
  },

  async downloadReport(reportId) {
    const res = await authService.authenticatedRequest(`/api/admin/reports/${reportId}/download`, { method: 'GET' });
    if (!res.ok) throw new Error(`Error descargando reporte: ${res.status}`);
    return res.blob();
  },


  // Estudiante: descargar reporte narrativo de nivel (PDF)
  async downloadLevelReportPdf(nivelId) {
    const res = await authService.authenticatedRequest(`/api/courses/levels/${nivelId}/report/pdf`, { method: 'GET' });
    if (!res.ok) {
      const raw = await res.text().catch(() => '');
      let data;
      try { data = raw ? JSON.parse(raw) : undefined; } catch (_) { }
      const msg = data?.message || data?.error || raw || `Error descargando reporte: ${res.status}`;
      throw new Error(msg);
    }

    const blob = await res.blob();
    const dispo = res.headers.get('Content-Disposition') || '';
    const m = dispo.match(/filename="([^"]+)"/i) || dispo.match(/filename=([^;]+)/i);
    const filename = (m && m[1]) ? String(m[1]).trim() : `reporte_nivel_${nivelId}.pdf`;
    return { blob, filename };
  },

  // === BACKUP Y MANTENIMIENTO ===

  async createBackup() {
    return authenticatedRequest(`/api/admin/backup`, { method: 'POST' });
  },

  async getBackups() {
    return authenticatedRequest(`/api/admin/backup`);
  },

  async restoreBackup(backupId) {
    return authenticatedRequest(`/api/admin/backup/${backupId}/restore`, { method: 'POST' });
  },

  // === LOGS Y AUDITORÍA ===

  async getSystemLogs({ page = 1, limit = 50, level = '', dateFrom = '', dateTo = '' }) {
    const params = new URLSearchParams({
      page, limit,
      ...(level && { level }),
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
    });
    return authenticatedRequest(`/api/admin/logs?${params.toString()}`);
  },

  async getAuditLogs({ page = 1, limit = 50, action = '', userId = '', dateFrom = '', dateTo = '' }) {
    const params = new URLSearchParams({
      page, limit,
      ...(action && { action }),
      ...(userId && { userId }),
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
    });
    return authenticatedRequest(`/api/admin/audit?${params.toString()}`);
  },

  // === NOTIFICACIONES ===

  async sendNotification(notificationData) {
    return authenticatedRequest(`/api/admin/notifications`, {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
  },

  async getSentNotifications({ page = 1, limit = 20 }) {
    const params = new URLSearchParams({ page, limit });
    return authenticatedRequest(`/api/admin/notifications?${params.toString()}`);
  },

  // === ESTADÍSTICAS AVANZADAS ===

  async getUsageStats({ period = '30d', type = 'all' }) {
    const params = new URLSearchParams({ period, type });
    return authenticatedRequest(`/api/admin/stats/usage?${params.toString()}`);
  },

  async getPerformanceStats({ period = '24h' }) {
    const params = new URLSearchParams({ period });
    return authenticatedRequest(`/api/admin/stats/performance?${params.toString()}`);
  },

  async getRealTimeMetrics() {
    return authenticatedRequest(`/api/admin/metrics/realtime`);
  },

  async addUserRole(userId, role) {
    try {
      return await authenticatedRequest(`/api/admin/users/${userId}/roles`, {
        method: 'POST',
        body: JSON.stringify({ role }),
      });
    } catch (error) {
      console.error('❌ Error adding user role:', error);
      throw error;
    }
  },

  async removeUserRole(userId, role) {
    try {
      return await authenticatedRequest(`/api/admin/users/${userId}/roles/${role}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('❌ Error removing user role:', error);
      throw error;
    }
  },

  async getUserRoles(userId) {
    try {
      return await authenticatedRequest(`/api/admin/users/${userId}/roles`);
    } catch (error) {
      console.error('❌ Error getting user roles:', error);
      throw error;
    }
  },

  // === GESTIÓN DE DOCENTES ===

  async updateTeacherAssignment(assignmentId, assignmentData) {
    const payload = {
      rol: assignmentData.rol,
      activo: assignmentData.activo,
    };

    return authenticatedRequest(`/api/admin/teacher-assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },


  async removeTeacherFromCourse(assignmentId) {
    await authService.authenticatedRequest(`/api/admin/teacher-assignments/${assignmentId}`, { method: 'DELETE' });
    return { success: true };
  },


  async getTeacherCourses(teacherId, { page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    return authenticatedRequest(`/api/admin/teachers/${teacherId}/courses?${params.toString()}`);
  },

  async findUserForImport({
    email = "",
    codigo_institucional = "",
    nombres = "",
    apellidos = "",
    facultad_id,
  } = {}) {
    const params = new URLSearchParams({
      ...(email && { email }),
      ...(codigo_institucional && { codigo_institucional }),
      ...(nombres && { nombres }),
      ...(apellidos && { apellidos }),
    });

    if (facultad_id != null && facultad_id !== "") {
      params.set("facultad_id", String(facultad_id));
    }

    return authenticatedRequest(`/api/admin/users/find-for-import?${params.toString()}`);
  },

  async fetchTeachers({ page = 1, limit = 20, search = '', department = '', includeAssignments = true, facultad_id } = {}) {
    const params = new URLSearchParams({
      page, limit,
      ...(search && { search }),
      ...(department && { department }),
      ...(includeAssignments && { include_assignments: 'true' }),
    });

    if (facultad_id != null && facultad_id !== "") {
      params.set("facultad_id", String(facultad_id));
    }

    return authenticatedRequest(`/api/admin/teachers?${params.toString()}`);
  },

  async fetchUserStats({ facultad_id } = {}) {
    const params = new URLSearchParams();
    if (facultad_id != null && facultad_id !== "") {
      params.set("facultad_id", String(facultad_id));
    }
    const qs = params.toString();
    return authenticatedRequest(`/api/admin/users/stats${qs ? `?${qs}` : ""}`);
  },

  // === GESTIÓN DE ESTUDIANTES ===

  async enrollStudentToCourse(courseId, studentData) {
    return authenticatedRequest(`/api/courses/${courseId}/enroll-student`, {
      method: 'POST',
      body: JSON.stringify(studentData),
    });
  },

  async updateStudentEnrollment(enrollmentId, enrollmentData) {
    return authenticatedRequest(`/api/admin/student-enrollments/${enrollmentId}`, {
      method: 'PUT',
      body: JSON.stringify(enrollmentData),
    });
  },

  async removeStudentFromCourse(enrollmentId) {
    await authService.authenticatedRequest(`/api/admin/student-enrollments/${enrollmentId}`, { method: 'DELETE' });
    return { success: true };
  },

  async getStudentCourses(studentId, { page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    return authenticatedRequest(`/api/admin/students/${studentId}/courses?${params.toString()}`);
  },

  // === HORARIOS DE EXAMEN ===

  async fetchExamSchedules(params = {}) {
    try {
      const qp = new URLSearchParams();
      qp.set('page', String(params.page ?? 1));
      qp.set('limit', String(params.limit ?? 10));

      const appendIf = (k, v) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          qp.append(k, String(v).trim());
        }
      };

      appendIf('search', params.search);
      appendIf('status', params.status);

      if (!params.fecha_desde && !params.fecha_hasta) appendIf('fecha', params.fecha);
      appendIf('curso_id', params.curso_id);
      appendIf('codigo_curso', params.codigo_curso);
      appendIf('fecha_desde', params.fecha_desde);
      appendIf('fecha_hasta', params.fecha_hasta);

      // ✅ NUEVO: modalidad
      // Ojo: NO uses appendIf porque "false" podría ser descartado si llega boolean false.
      // Aquí lo controlamos explícitamente:
      if (params.is_virtual === true) qp.set('is_virtual', 'true');
      else if (params.is_virtual === false) qp.set('is_virtual', 'false');
      ;

      return authenticatedRequest(`/api/admin/exam-schedules?${qp.toString()}`);
    } catch (error) {
      console.error('Error en fetchExamSchedules:', error);
      throw error;
    }
  }
  ,

  async setStudentExamEnabled(examId, studentId, habilitado, userid) {
    return authenticatedRequest(`/api/admin/exam-schedules/${examId}/students/${studentId}/habilitar`, {
      method: 'POST',
      body: JSON.stringify({ habilitado, userid }),
    });
  },

  async getTaskSubmission(courseId, tareaId) {
    return authenticatedRequest(`/api/users/courses/${courseId}/tareas/${tareaId}/submission`);
  },

  async fetchExamStats() {
    try {
      return await authenticatedRequest(`/api/admin/exam-schedules/stats`);
    } catch (error) {
      console.error('Error en fetchExamStats:', error);
      throw error;
    }
  },

  async createExamSchedule(scheduleData) {
    try {
      return await authenticatedRequest(`/api/admin/exam-schedules`, {
        method: 'POST',
        body: JSON.stringify(scheduleData),
      });
    } catch (error) {
      console.error('Error en createExamSchedule:', error);
      throw error;
    }
  },

  async fetchExamAssistantOptions(params = {}) {
    try {
      const cursoId = Number(
        params?.curso_id ??
        params?.cursoId ??
        params?.course_id ??
        params?.courseId
      );

      // ✅ No es error real: simplemente no hay curso seleccionado.
      if (!Number.isFinite(cursoId) || cursoId <= 0) {
        return [];
      }

      const qp = new URLSearchParams();
      qp.set("curso_id", String(cursoId));

      if (params?.search && String(params.search).trim()) {
        qp.set("search", String(params.search).trim());
      }

      const response = await authenticatedRequest(
        `/api/admin/exam-assistant-options?${qp.toString()}`,
        { method: "GET" }
      );

      return Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response?.assistants)
            ? response.assistants
            : Array.isArray(response?.assistantOptions)
              ? response.assistantOptions
              : [];
    } catch (error) {
      console.error("Error en fetchExamAssistantOptions:", error);
      throw error;
    }
  },



  async updateExamSchedule(scheduleId, updateData) {
    try {
      return await authenticatedRequest(`/api/admin/exam-schedules/${scheduleId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
    } catch (error) {
      console.error('Error en updateExamSchedule:', error);
      throw error;
    }
  },

  async deleteExamSchedule(scheduleId) {
    try {
      await authService.authenticatedRequest(`/api/admin/exam-schedules/${scheduleId}`, { method: 'DELETE' });
      return { success: true };
    } catch (error) {
      console.error('Error en deleteExamSchedule:', error);
      throw error;
    }
  },


  async fetchCourseExamSchedules(cursoId, opts = {}) {
    const params = new URLSearchParams();
    params.set('curso_id', cursoId);
    if (opts.status) params.set('status', opts.status);
    if (opts.upcoming) params.set('upcoming', '1');

    return authenticatedRequest(
      `/api/users/exam-schedules?${params.toString()}`,
      { method: 'GET' }
    );
  },


  async fetchMyExamBookings(params = {}) {
    const qp = new URLSearchParams();
    if (params.curso_id != null) qp.set('curso_id', String(params.curso_id));
    if (params.upcoming != null) qp.set('upcoming', params.upcoming ? '1' : '0');
    if (params.status) qp.set('status', String(params.status));
    if (params.page) qp.set('page', String(params.page));
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.user_id != null) qp.set('user_id', String(params.user_id));

    const basePath = `/api/users/student/exam-bookings`;
    const url = qp.toString() ? `${basePath}?${qp.toString()}` : basePath;

    return authenticatedRequest(url, { method: 'GET' });
  },

  // Llama a esto después de crear/cancelar agendamientos para no servir datos viejos
  invalidateExamBookingsCache(/* userId */) {
    // no-op (sin caché para bookings de examen)
  },


  async selfScheduleExam(horarioId, userId, acceptTerms = false) {
    const payload = { horario_examen_id: Number(horarioId) };
    if (userId != null) payload.user_id = Number(userId);
    if (acceptTerms === true) payload.acceptTerms = true;

    return authenticatedRequest(`/api/users/student/exam-bookings`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async getExamTermsCurrent() {
    return authenticatedRequest(`/api/exams/terms/current`, {
      method: 'GET',
    });
  },

  async cancelMyExamBooking(bookingId, userID) {
    return authenticatedRequest(`/api/users/student/exam-bookings/${Number(bookingId)}/${Number(userID)}`, {
      method: 'DELETE',
    });
  },


  // === GESTIÓN DE ESTUDIANTES EN EXÁMENES ===

  async fetchExamStudents(scheduleId) {
    try {
      return await authenticatedRequest(`/api/admin/exam-schedules/${scheduleId}/students`);
    } catch (error) {
      console.error('Error en fetchExamStudents:', error);
      throw error;
    }
  },

  async fetchAvailableStudents(scheduleId) {
    try {
      return await authenticatedRequest(`/api/admin/exam-schedules/${scheduleId}/available-students`);
    } catch (error) {
      console.error('Error en fetchAvailableStudents:', error);
      throw error;
    }
  },

  async addStudentToExam(scheduleId, studentId) {
    try {
      return await authenticatedRequest(`/api/admin/exam-schedules/${scheduleId}/students`, {
        method: 'POST',
        body: JSON.stringify({ estudiante_id: studentId }),
      });
    } catch (error) {
      console.error('Error en addStudentToExam:', error);
      throw error;
    }
  },

  async removeStudentFromExam(scheduleId, studentId) {
    try {
      await authService.authenticatedRequest(`/api/admin/exam-schedules/${scheduleId}/students/${studentId}`, {
        method: 'DELETE',
      });
      return { success: true };
    } catch (error) {
      console.error('Error en removeStudentFromExam:', error);
      throw error;
    }
  },

  async updateStudentExamStatus(scheduleId, studentId, newStatus) {
    try {
      return await authenticatedRequest(`/api/admin/exam-schedules/${scheduleId}/students/${studentId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ estado: newStatus }),
      });
    } catch (error) {
      console.error('Error en updateStudentExamStatus:', error);
      throw error;
    }
  },

  async resolveStudentCourseByCode(codigoCurso) {
    const res = await authService.authenticatedRequest(`/api/courses/student?page=1&limit=200`);
    const data = await handleResponse(res);
    const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : (data?.courses || []));
    if (!items || !items.length) return null;

    const found = items.find(c =>
      String(c.codigo_curso || c.codigo || c.code || '').toLowerCase() === String(codigoCurso).toLowerCase()
    );
    if (!found) return null;
    return {
      id: found.id ?? found.curso_id ?? found.courseId,
      codigo_curso: found.codigo_curso ?? found.codigo ?? found.code ?? String(codigoCurso).toUpperCase(),
      nombre: found.nombre ?? found.curso_nombre ?? found.title ?? 'Curso',
      completado:
        (typeof found.estado === 'string' && found.estado.toLowerCase() === 'aprobado') ||
        found.completado === true,
      tipo: found.tipo,
    };
  },

  // === TIPOS DE CONTENIDO ===

  async getAllowedContentTypes() {
    return authenticatedRequest(`/api/admin/content-types/allowed`);
  },

  async updateAllowedContentTypes(tipos_permitidos) {
    return authenticatedRequest(`/api/admin/content-types/allowed`, {
      method: 'PUT',
      body: JSON.stringify({ tipos_permitidos }),
    });
  },

  async getAllStructureTypes() {
    return authenticatedRequest(`/api/admin/content-types/all`);
  },

  async validateContentTypesConfiguration() {
    return authenticatedRequest(`/api/admin/content-types/validate`);
  },

  // === CONFIGURACIÓN DEL SISTEMA ===

  async getSystemConfigurations() {
    return authenticatedRequest(`/api/admin/configuracion`);
  },

  async updateSystemConfiguration(clave, valor) {
    return authenticatedRequest(`/api/admin/configuracion/${clave}`, {
      method: 'PUT',
      body: JSON.stringify({ valor }),
    });
  },

  // Estructura (student)
  async getCourseStructureStudent(courseId, { ttlMs = 0 } = {}) {
    const url = `/api/courses/${courseId}/structure`;
    if (ttlMs <= 0) {
      return authenticatedRequest(url);
    }

    const currentUser = authService.getCurrentUser?.() || {};
    const userId =
      currentUser.id ?? currentUser.usuario_id ?? currentUser.user_id ?? "me";

    const cacheKey = makeKey({ url, userId });
    const cached = getCached(cacheKey);

    if (ttlMs > 0 && isFresh(cached)) return cached.data;

    return singleFlight(cacheKey, async () => {
      try {
        const extraHeaders = {};
        if (cached?.etag) extraHeaders["If-None-Match"] = cached.etag;

        const res = await authService.authenticatedRequest(url, {
          headers: extraHeaders,
        });

        if (res.status === 304 && cached?.data) {
          touch(cacheKey);
          return cached.data;
        }

        const data = await handleResponse(res);
        setCached(cacheKey, {
          data,
          etag: res.headers.get("ETag"),
          ttlMs,
        });
        return data;
      } catch (error) {
        console.error("Error obteniendo estructura del curso:", error);
        throw error;
      }
    });
  },

  async getCourseStructureAdminPreview(courseId) {
    return authenticatedRequest(`/api/courses/${courseId}/estructura/admin`);
  },

  async getStudentProgress(courseId, studentId = null) {
    try {
      const url = studentId
        ? `/api/courses/${courseId}/progress/student/${studentId}`
        : `/api/courses/${courseId}/progress`;
      return await authenticatedRequest(url);
    } catch (error) {
      console.error('Error obteniendo progreso del estudiante:', error);
      throw error;
    }
  },

  async updateElementProgress(estructuraId, progressData) {
    try {
      return await authenticatedRequest(`/api/progress/element/${estructuraId}`, {
        method: 'PUT',
        body: JSON.stringify(progressData),
      });
    } catch (error) {
      console.error('Error actualizando progreso del elemento:', error);
      throw error;
    }
  },

  async completeElement(estructuraId, completionData = {}) {
    try {
      return await authenticatedRequest(`/api/progress/element/${estructuraId}/complete`, {
        method: 'POST',
        body: JSON.stringify({
          puntuacion: completionData.puntuacion || 100,
          tiempo_empleado_segundos: completionData.tiempo_empleado || 0,
          datos_adicionales: completionData.datos_adicionales || {},
        }),
      });
    } catch (error) {
      console.error('Error completando elemento:', error);
      throw error;
    }
  },

  async unlockElement(estudianteId, estructuraId) {
    try {
      return await authenticatedRequest(`/api/progress/unlock`, {
        method: 'POST',
        body: JSON.stringify({
          estudiante_id: estudianteId,
          estructura_id: estructuraId,
        }),
      });
    } catch (error) {
      console.error('Error desbloqueando elemento:', error);
      throw error;
    }
  },

  async getUnlockedElements(courseId, studentId = null) {
    try {
      const url = studentId
        ? `/api/courses/${courseId}/unlocked/student/${studentId}`
        : `/api/courses/${courseId}/unlocked`;
      return await authenticatedRequest(url);
    } catch (error) {
      console.error('Error obteniendo elementos desbloqueados:', error);
      throw error;
    }
  },

  async resetStudentProgress(courseId, studentId) {
    try {
      return await authenticatedRequest(`/api/admin/courses/${courseId}/students/${studentId}/reset-progress`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error reiniciando progreso:', error);
      throw error;
    }
  },

  async getCourseProgressStats(courseId) {
    try {
      return await authenticatedRequest(`/api/admin/courses/${courseId}/progress-stats`);
    } catch (error) {
      console.error('Error obteniendo estadísticas de progreso:', error);
      throw error;
    }
  },

  // === ACTIVIDADES Y TAREAS ===

  async submitActivityAnswer(actividadId, answerData) {
    try {
      return await authenticatedRequest(`/api/activities/${actividadId}/answer`, {
        method: 'POST',
        body: JSON.stringify(answerData),
      });
    } catch (error) {
      console.error('Error enviando respuesta de actividad:', error);
      throw error;
    }
  },

  async getActivityQuestions(actividadId) {
    try {
      return await authenticatedRequest(`/api/activities/${actividadId}/questions`);
    } catch (error) {
      console.error('Error obteniendo preguntas de actividad:', error);
      throw error;
    }
  },

  async completeActivityGame(actividadId, gameData) {
    try {
      return await authenticatedRequest(`/api/activities/${actividadId}/game/complete`, {
        method: 'POST',
        body: JSON.stringify(gameData),
      });
    } catch (error) {
      console.error('Error completando juego de actividad:', error);
      throw error;
    }
  },

  async getActivityGameConfig(actividadId) {
    try {
      return await authenticatedRequest(`/api/activities/${actividadId}/game`);
    } catch (error) {
      console.error('Error obteniendo configuración de juego:', error);
      throw error;
    }
  },

  // Crear juego dentro de una estructura (admin)
  async adminCreateStructureGame(estructuraId, payload) {
    const hasFile = payload && payload.media_file; // File o Blob

    if (hasFile) {
      const fd = new FormData();
      fd.append("tipo_juego", payload.tipo_juego);
      fd.append("instrucciones", payload.instrucciones || "");
      fd.append("puntuacion_maxima", payload.puntuacion_maxima ?? 100);
      if (payload.tiempo_limite_segundos != null) {
        fd.append("tiempo_limite_segundos", payload.tiempo_limite_segundos);
      }
      fd.append("activo", String(payload.activo ?? true));
      fd.append("ia", String(payload.ia ?? false));
      fd.append("configuracion", JSON.stringify(payload.configuracion || {}));

      // Campos del adjunto
      if (payload.media_type) fd.append("media_type", payload.media_type);
      if (payload.media_alt) fd.append("media_alt", payload.media_alt);
      if (payload.media_caption) fd.append("media_caption", payload.media_caption);
      fd.append("media_file", payload.media_file);
      if (payload.remove_media) fd.append("remove_media", String(payload.remove_media));

      return authenticatedRequest(
        `/api/admin/structure/${encodeURIComponent(estructuraId)}/games`,
        { method: "POST", body: fd }
      );
    }

    // JSON normal (sin archivo)
    return authenticatedRequest(
      `/api/admin/structure/${encodeURIComponent(estructuraId)}/games`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
  },

  // Actualizar juego (admin)
  async adminUpdateGame(gameId, payload) {
    const hasFile = payload && payload.media_file;

    if (hasFile) {
      const fd = new FormData();
      fd.append("tipo_juego", payload.tipo_juego);
      fd.append("instrucciones", payload.instrucciones || "");
      if (payload.puntuacion_maxima != null) fd.append("puntuacion_maxima", payload.puntuacion_maxima);
      if (payload.tiempo_limite_segundos != null) fd.append("tiempo_limite_segundos", payload.tiempo_limite_segundos);
      if (payload.activo != null) fd.append("activo", String(payload.activo));
      if (payload.ia != null) fd.append("ia", String(payload.ia));
      fd.append("configuracion", JSON.stringify(payload.configuracion || {}));

      if (payload.media_type) fd.append("media_type", payload.media_type);
      if (payload.media_alt) fd.append("media_alt", payload.media_alt);
      if (payload.media_caption) fd.append("media_caption", payload.media_caption);
      fd.append("media_file", payload.media_file);
      if (payload.remove_media) fd.append("remove_media", String(payload.remove_media));

      return authenticatedRequest(`/api/admin/games/${encodeURIComponent(gameId)}`, {
        method: "PUT",
        body: fd,
      });
    }

    // JSON normal
    return authenticatedRequest(`/api/admin/games/${encodeURIComponent(gameId)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  },



  // === TESTS DE NIVEL ===

  async getEnabledTests(courseId) {
    try {
      return await authenticatedRequest(`/api/courses/${courseId}/tests/enabled`);
    } catch (error) {
      console.error('Error obteniendo tests habilitados:', error);
      throw error;
    }
  },

  async startTestPresentation(testId) {
    try {
      return await authenticatedRequest(`/api/tests/${testId}/start`, { method: 'POST' });
    } catch (error) {
      console.error('Error iniciando test:', error);
      throw error;
    }
  },

  async submitTestAnswers(presentacionId, answers) {
    try {
      return await authenticatedRequest(`/api/test-presentations/${presentacionId}/submit`, {
        method: 'POST',
        body: JSON.stringify({ respuestas: answers }),
      });
    } catch (error) {
      console.error('Error enviando respuestas de test:', error);
      throw error;
    }
  },

  async getTestResults(presentacionId) {
    try {
      return await authenticatedRequest(`/api/test-presentations/${presentacionId}/results`);
    } catch (error) {
      console.error('Error obteniendo resultados de test:', error);
      throw error;
    }
  },

  // === GAMIFICACIÓN ===

  async getStudentUTMCoins() {
    try {
      return await authenticatedRequest(`/api/gamification/utmcoins`);
    } catch (error) {
      console.error('Error obteniendo UTMCoins:', error);
      throw error;
    }
  },

  async getUTMCoinsHistory(page = 1, limit = 10) {
    try {
      const params = new URLSearchParams({ page, limit });
      return await authenticatedRequest(`/api/gamification/utmcoins/history?${params}`);
    } catch (error) {
      console.error('Error obteniendo historial de UTMCoins:', error);
      throw error;
    }
  },

  async getStudentStreak() {
    try {
      return await authenticatedRequest(`/api/gamification/streak`);
    } catch (error) {
      console.error('Error obteniendo racha del estudiante:', error);
      throw error;
    }
  },

  async registerDailyConnection() {
    // Si activas el endpoint, cambia por authenticatedRequest con POST
    return 1;
  },

  // === ADMINISTRACIÓN DE PROGRESO ===

  async getProgressReport(courseId, filters = {}) {
    try {
      const params = new URLSearchParams({
        ...(filters.studentId && { studentId: filters.studentId }),
        ...(filters.structureType && { structureType: filters.structureType }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
        page: filters.page || 1,
        limit: filters.limit || 20,
      });
      return await authenticatedRequest(`/api/admin/courses/${courseId}/progress-report?${params}`);
    } catch (error) {
      console.error('Error obteniendo reporte de progreso:', error);
      throw error;
    }
  },

  async exportProgressData(courseId, format = 'xlsx') {
    try {
      const res = await authService.authenticatedRequest(
        `/api/admin/courses/${courseId}/progress-export?format=${format}`,
        { method: 'GET' }
      );
      if (!res.ok) throw new Error(`Error exportando datos: ${res.status}`);
      return res.blob();
    } catch (error) {
      console.error('Error exportando datos de progreso:', error);
      throw error;
    }
  },

  // === EXÁMENES ===
  async fetchExams({ page = 1, limit = 20, search = '', curso_id = '', activo = '' } = {}) {
    const params = new URLSearchParams({
      page, limit,
      ...(search && { search }),
      ...(curso_id && { curso_id }),
      ...(activo !== '' && { activo }),
    });
    return authenticatedRequest(`/api/exams?${params.toString()}`);
  },

  async createExam(examData) {
    return authenticatedRequest(`/api/exams`, {
      method: 'POST',
      body: JSON.stringify(examData),
    });
  },

  async updateExam(id, examData) {
    return authenticatedRequest(`/api/exams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(examData),
    });
  },

  async deleteExam(id) {
    const res = await authService.authenticatedRequest(`/api/exams/${id}`, { method: 'DELETE' });
    if (res.status === 404) throw new Error('Examen no encontrado');
    if (!res.ok) throw new Error(`Error al eliminar examen: ${res.status}`);
    return { success: true };
  },

  // === PREGUNTAS DEL EXAMEN ===
  async fetchExamQuestions(examId) {
    return authenticatedRequest(`/api/exams/schedules/${examId}/questions`); // devuelve []
  },

  async addCustomExamQuestion(examId, payload) {
    return authenticatedRequest(`/api/exams/${examId}/questions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async cloneExamQuestion(examId, payload) {
    return authenticatedRequest(`/api/exams/${examId}/questions/clone`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async deleteExamRef(examId, refId) {
    return authenticatedRequest(`/api/exams/${examId}/question-refs/${refId}`, {
      method: 'DELETE',
    });
  },

  async fetchExamSchedulesByCourse(cursoId, { soloActivos = true } = {}) {
    return authenticatedRequest(`/api/exams/schedules?curso_id=${cursoId}&solo_activos=${soloActivos}`);
  },

  async fetchExamTerms(horarioId) {
    try {
      return await authenticatedRequest(`/api/exams/schedules/${horarioId}/terms`);
    } catch (error) {
      console.error('Error en fetchExamTerms:', error);
      throw error;
    }
  },

  async acceptExamTerms(horarioId) {
    try {
      return await authenticatedRequest(`/api/exams/schedules/${horarioId}/terms/accept`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error en acceptExamTerms:', error);
      throw error;
    }
  },

  async validateExamAccessCode(examId, payload = {}) {
    const res = await authService.authenticatedRequest(
      `/api/exams/${Number(examId)}/validate-code`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload || {}),

        // Este endpoint no debe reintentarse.
        noRetry: true,
        skipRetry: true,
        retry: false,
        retries: 0,
        maxRetries: 0,

        // Este endpoint no debe activar refresh-token por 401.
        skipAuthRefresh: true,
        noAuthRefresh: true,

        // Tampoco debe cachearse ni deduplicarse.
        dedupe: false,
        cache: "no-store",
      }
    );

    return handleResponse(res);
  },
  // Permite pools desde estructura O desde banco
  async createExamQuestionPool(examId, payload) {
    // payload puede traer:
    //  - fuente: 'estructura_preguntas' | 'banco_preguntas'
    //  - estructura_id (si fuente=estructura_preguntas)
    //  - bank_id (si fuente=banco_preguntas)
    //  - include_subtree (true en estructura, false en banco)
    //  - cantidad
    //  - criterios (obj)
    //  - scope (opcional: 'modulo'|'componente'|'banco')

    const body = {
      fuente: payload.fuente ?? 'estructura_preguntas',
      cantidad: Number(payload.cantidad),
      criterios: payload.criterios ?? null,
      // en banco no aplica subtree; en estructura respeta lo que venga
      include_subtree:
        (payload.fuente === 'banco_preguntas') ? false : !!payload.include_subtree,
      ...(payload.estructura_id != null ? { estructura_id: Number(payload.estructura_id) } : {}),
      ...(payload.bank_id != null ? { bank_id: Number(payload.bank_id) } : {}),
      ...(payload.scope ? { scope: payload.scope } : {}),
    };

    return authenticatedRequest(`/api/exams/${examId}/question-pools`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  async updateExamQuestion(examId, questionId, payload) {
    return authenticatedRequest(`/api/exams/${examId}/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },


  async uploadProctorSnapshot({
    blob,
    presentacionId = null,        // examen: presentacion_id
    examId = null,
    courseId = null,
    horarioExamenId = null,

    // ✅ NUEVO (nivel test)
    context = "examen",           // 'examen' | 'nivel_test' ...
    testPresentacionId = null,    // test_presentacion_id
    testId = null,               // test_id

    // opcionales (debug / auditoría)
    tag = null,
    codigoCurso = null,
    meta = null,

    mic = null,
    final = 0
  }) {
    const fd = new FormData();
    fd.append("photo", blob, `snapshot-${Date.now()}.jpg`);

    // ✅ contexto
    if (context) fd.append("contexto", String(context));

    // examen (lo de siempre)
    if (presentacionId != null) fd.append("presentacion_id", String(presentacionId));
    if (examId != null) fd.append("exam_id", String(examId));
    if (courseId != null) fd.append("course_id", String(courseId));
    if (horarioExamenId != null) fd.append("horario_examen_id", String(horarioExamenId));

    // ✅ nivel test
    if (testPresentacionId != null) fd.append("test_presentacion_id", String(testPresentacionId));
    if (testId != null) fd.append("test_id", String(testId));

    // extras
    if (tag) fd.append("tag", String(tag));
    if (codigoCurso) fd.append("codigo_curso", String(codigoCurso));
    if (meta != null) fd.append("meta", JSON.stringify(meta));

    if (final != null) fd.append("final", String(final));

    // mic (igual)
    if (mic) {
      if (mic.mic_enabled != null) fd.append("mic_enabled", mic.mic_enabled ? "true" : "false");
      if (mic.mic_permission_state != null) fd.append("mic_permission_state", String(mic.mic_permission_state));
      if (mic.mic_device_id != null) fd.append("mic_device_id", String(mic.mic_device_id));
      if (mic.mic_device_label != null) fd.append("mic_device_label", String(mic.mic_device_label));

      if (mic.mic_duration_ms != null) fd.append("mic_duration_ms", String(mic.mic_duration_ms));
      if (mic.mic_sample_rate_hz != null) fd.append("mic_sample_rate_hz", String(mic.mic_sample_rate_hz));
      if (mic.mic_samples_count != null) fd.append("mic_samples_count", String(mic.mic_samples_count));

      if (mic.mic_avg_db != null) fd.append("mic_avg_db", String(mic.mic_avg_db));
      if (mic.mic_max_db != null) fd.append("mic_max_db", String(mic.mic_max_db));
      if (mic.mic_min_db != null) fd.append("mic_min_db", String(mic.mic_min_db));

      if (mic.mic_silence_pct != null) fd.append("mic_silence_pct", String(mic.mic_silence_pct));
      if (mic.mic_clip_count != null) fd.append("mic_clip_count", String(mic.mic_clip_count));
      if (mic.mic_dropouts_count != null) fd.append("mic_dropouts_count", String(mic.mic_dropouts_count));

      if (mic.mic_metrics != null) fd.append("mic_metrics", JSON.stringify(mic.mic_metrics));
    }

    return authenticatedRequest("/api/exams/snapshots", { method: "POST", body: fd })
      .then(async (r) => {
        // si authenticatedRequest ya devuelve json, esto no hace daño:
        if (r && typeof r === "object" && ("ok" in r || "ok_db" in r)) return r;

        // si devolvió fetch Response:
        if (r && typeof r.json === "function") {
          const j = await r.json().catch(() => null);
          return j ?? { ok: false, error: "NO_JSON" };
        }

        return r;
      });
  },

  async fetchProctoringReviewQueue(horarioId, params = {}) {
    try {
      const query = new URLSearchParams();

      if (params.estado) query.set("estado", params.estado);
      if (params.search) query.set("search", params.search);

      const qs = query.toString();

      const response = await authenticatedRequest(
        `/api/admin/exam-schedules/${horarioId}/proctoring-review-queue${qs ? `?${qs}` : ""}`,
        {
          method: "GET",
        }
      );

      return response;
    } catch (error) {
      console.error("Error en fetchProctoringReviewQueue:", error);
      throw error;
    }
  },

  async reviewProctoringPresentation(presentacionId, payload = {}) {
    try {
      const response = await authenticatedRequest(
        `/api/admin/proctoring/presentations/${presentacionId}/review`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        }
      );

      return response;
    } catch (error) {
      console.error("Error en reviewProctoringPresentation:", error);
      throw error;
    }
  },



  async uploadProctorMetrics({
    presentacionId,
    examId,
    courseId,
    horarioExamenId = null,
    kind = "proctor_minute",
    metrics,
  }) {
    return authenticatedRequest("/api/exams/proctoring/metrics", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        presentacion_id: presentacionId,
        exam_id: examId,
        course_id: courseId,
        horario_examen_id: horarioExamenId,
        kind,
        metrics,
      }),
    });
  },
  async deleteExamQuestion(examId, questionId) {
    const res = await authService.authenticatedRequest(`/api/exams/${examId}/questions/${questionId}`, {
      method: 'DELETE',
    });
    if (res.status === 404) throw new Error('Pregunta no encontrada');
    if (!res.ok) throw new Error(`Error al eliminar pregunta: ${res.status}`);
    return { success: true };
  },

  // === BÚSQUEDA DE PREGUNTAS FUENTE PARA CLONAR ===
  async browseStructureQuestions(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).reduce((acc, [k, v]) => {
        if (v !== undefined && v !== null && v !== '') acc[k] = v;
        return acc;
      }, {})
    );
    const res = await authService.authenticatedRequest(`/api/exams/sources/estructura_preguntas?${qs.toString()}`);
    return handleResponse(res);
  },

  async browseStructureMeta(cursoId) {
    const cid = Number(cursoId);

    // cursoId obligatorio y debe ser número
    if (!Number.isFinite(cid) || cid <= 0) {
      throw new Error(`cursoId inválido para estructura_meta: ${cursoId}`);
    }

    const qs = `?cursoId=${encodeURIComponent(String(cid))}`;
    const res = await authService.authenticatedRequest(`/api/exams/sources/estructura_meta${qs}`);
    return handleResponse(res);
  },

  async browseGameItemQuestions({ page = 1, limit = 10, search = '', tipo = '', nivel = '', juegoId = '' }) {
    const params = new URLSearchParams({
      page, limit,
      ...(search && { search }),
      ...(tipo && { tipo }),
      ...(nivel && { nivel }),
      ...(juegoId && { juegoId }),
    });
    return authenticatedRequest(`/api/exams/sources/juego_items?${params.toString()}`);
  },

  // FuncionService.js
  async reportTaskCompletion(data = {}) {
    const { curso_id, tarea_id } = data;
    if (!curso_id) throw new Error("Falta curso_id");
    if (!tarea_id) throw new Error("Falta tarea_id");

    const url = `/api/activities/courses/${Number(curso_id)}/tasks/${Number(tarea_id)}/complete`;
    const pick = (...xs) => xs.find(v => v !== undefined && v !== null);

    const toAnswer = (a = {}) => {
      const rawId = a.actividad_id ?? a.id ?? a.actividadId ?? a.estructura_pregunta_id;
      if (rawId === '' || rawId == null) return null;

      const actividad_id = Number.isFinite(Number(rawId)) ? Number(rawId) : String(rawId);
      const tipo = String(a.tipo_pregunta ?? a.tipo ?? 'respuesta_corta').toLowerCase();

      let respuesta = null;
      switch (tipo) {
        case 'respuesta_corta': {
          const val = pick(a.respuesta_usuario, a.userAnswer, a.respuesta, a.value, a.text, '');
          respuesta = String(val).trim();
          break;
        }
        case 'verdadero_falso': {
          const val = pick(a.respuesta_usuario, a.userAnswer, a.respuesta, a.valor, a.value, false);
          const bool = (typeof val === 'boolean') ? val : String(val).toLowerCase().startsWith('v');
          // Si tu API espera booleanos, usa: respuesta = bool;
          respuesta = bool ? 'verdadero' : 'falso';
          break;
        }
        case 'opcion_multiple':
        case 'seleccion_simple': {
          const opt = pick(a.opcion_id, a.selectedOptionId, a.respuesta, a.userAnswer, a.valor, a.value);
          respuesta = Number.isFinite(Number(opt)) ? { opcion_id: Number(opt) } : { valor: String(opt ?? '') };
          break;
        }
        case 'seleccion_multiple': {
          const arr = pick(a.selectedOptionIds, a.respuesta, a.userAnswer, a.valores, a.values, []);
          const list = Array.isArray(arr) ? arr : [arr];
          respuesta = list.map(x => Number.isFinite(Number(x)) ? { opcion_id: Number(x) } : { valor: String(x ?? '') });
          break;
        }
        case 'completar': {
          const huecos = pick(a.huecos, a.respuesta, a.userAnswer, []);
          const list = Array.isArray(huecos) ? huecos : [];
          respuesta = {
            huecos: list.map(h => ({
              indice: Number(h.indice ?? h.index ?? h.hueco ?? 0),
              valor: String(h.valor ?? h.value ?? '').trim()
            }))
          };
          break;
        }
        case 'relacionar': {
          const pares = pick(a.pares, a.respuesta, a.userAnswer, []);
          const list = Array.isArray(pares) ? pares : [];
          respuesta = {
            pares: list.map(p => {
              const izq = p.izq ?? p.left ?? p.leftId ?? p.id_izq;
              const der = p.der ?? p.right ?? p.rightId ?? p.id_der;
              const L = Number.isFinite(Number(izq)) ? { id: Number(izq) } : { valor: String(izq ?? '') };
              const R = Number.isFinite(Number(der)) ? { id: Number(der) } : { valor: String(der ?? '') };
              return { izq: L, der: R };
            })
          };
          break;
        }
        case 'ordenar': {
          const orden = pick(a.orden, a.respuesta, a.userAnswer, []);
          const list = Array.isArray(orden) ? orden : [orden];
          respuesta = { orden: list.map(x => Number.isFinite(Number(x)) ? { id: Number(x) } : { valor: String(x ?? '') }) };
          break;
        }
        default: {
          respuesta = pick(a.respuesta_usuario, a.userAnswer, a.respuesta, a.value, a.text, null);
        }
      }
      return { actividad_id, tipo_pregunta: tipo, respuesta };
    };

    const answers = Array.isArray(data.answers)
      ? data.answers.map(toAnswer).filter(Boolean)
      : Array.isArray(data.respuestas)
        ? data.respuestas.map(toAnswer).filter(Boolean)
        : [];

    const started_at = data.started_at ?? new Date().toISOString();
    const finished_at = data.finished_at ?? new Date().toISOString();
    const meta = data.meta ?? undefined;
    const component_id = data.component_id ?? data.componentId ?? undefined;

    // ===== FLUJO PREGUNTAS (answers) =====
    if (answers.length > 0) {
      const total = data.total_actividades ?? answers.length;
      const payload = {
        answers,
        completada: data.completada ?? true,                 // <— OBLIGATORIO
        correctas: data.correctas ?? answers.length,        // opcional
        total_actividades: total,                            // opcional
        started_at,
        finished_at,
        meta,
        component_id
      };
      const res = await authService.authenticatedRequest(url, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return handleResponse(res);
    }

    // ===== FLUJO JUEGOS (sin answers) =====
    const acts = Array.isArray(data.actividades) ? data.actividades.map(a => ({
      id: a.id ?? a.actividad_id ?? a.id_actividad ?? a.key,
      tipo: a.tipo ?? 'juego',
      correct: !!a.correct,
      attempts: Number(a.attempts ?? 1),
    })) : [];

    if (acts.length > 0) {
      const payload = {
        completada: acts.every(x => x.correct),
        total_actividades: acts.length,
        correctas: acts.filter(x => x.correct).length,
        actividades: acts,
        started_at,
        finished_at,
        meta,
        component_id
      };
      const res = await authService.authenticatedRequest(url, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return handleResponse(res);
    }

    throw new Error('No hay respuestas ni actividades válidas para enviar');
  },

  // FuncionService.js
  async startTaskAttempt({ curso_id, tarea_id }) {
    if (!curso_id) throw new Error("Falta curso_id");
    if (!tarea_id) throw new Error("Falta tarea_id");

    const url = `/api/activities/courses/${Number(curso_id)}/tasks/${Number(tarea_id)}/attempts/start`;
    const res = await authService.authenticatedRequest(url, { method: "POST" });
    return handleResponse(res);
  },


  // === EXÁMENES (alumno/docente) ===

  async fetchStudentExams({ status = 'all', page = 1, limit = 20, strictTime = false, includeFinished = false } = {}) {
    const safeLimit = Math.max(1, Math.min(Number(limit) || 20, 100));
    const params = new URLSearchParams({
      status,
      page: String(page),
      limit: String(safeLimit),
      strictTime: String(!!strictTime),
      includeFinished: String(!!includeFinished),
    });
    return authenticatedRequest(`/api/exams/student/exams?${params.toString()}`);
  },

  async fetchCourseExams(courseId) {
    return authenticatedRequest(`/api/exams/course/${courseId}/exams`);
  },

  async getExamWithQuestions(examId, opts = {}) {
    const {
      shuffle = false,
      limit,
      revealAnswers = false,
      strictTime = true,
      qa = false,
      presentacionId, // ✅ NUEVO
    } = opts;

    const qs = new URLSearchParams();
    if (shuffle) qs.set('shuffle', 'true');
    if (revealAnswers) qs.set('revealAnswers', 'true');
    if (strictTime === false) qs.set('strictTime', 'false');
    if (limit) qs.set('limit', String(limit));
    if (qa) qs.set('qa', '1');

    // ✅ NUEVO: para que el backend “amarre” qué preguntas se sirvieron
    if (presentacionId && Number(presentacionId) > 0) qs.set('presentacionId', String(presentacionId));

    const url = `/api/exams/${examId}/with-questions${qs.toString() ? `?${qs.toString()}` : ''}`;
    return authenticatedRequest(url);
  }
  ,

  async startExam(examId, { horario_examen_id, horario_id, codigo, strictTime = true, qa } = {}) {
    const scheduleId = Number(horario_examen_id ?? horario_id);

    const payload = {
      horario_examen_id: Number.isFinite(scheduleId) ? scheduleId : undefined,
      horario_id: Number.isFinite(scheduleId) ? scheduleId : undefined,
      strictTime: qa ? false : !!strictTime,
    };

    const code = String(codigo ?? '').trim().slice(0, 10);
    if (code) payload.codigo = code;

    const qs = qa ? '?qa=1' : '';
    return authenticatedRequest(`/api/exams/${Number(examId)}/start${qs}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async scheduleExamPresentations(examId, { horario_examen_id }) {
    return authenticatedRequest(`/api/exams/${examId}/presentations/schedule`, {
      method: 'POST',
      body: JSON.stringify({ horario_examen_id }),
    });
  },

  async getCourseExamsWithQuestions(cursoId, { limitPerExam, revealAnswers = false } = {}) {
    const params = new URLSearchParams({ revealAnswers: String(!!revealAnswers) });
    if (Number(limitPerExam)) params.set('limitPerExam', String(Number(limitPerExam)));
    return authenticatedRequest(`/api/exams/by-course/${cursoId}/with-questions?${params.toString()}`);
  },

  async getModuleResources(estructuraId, { search = '', type = '' } = {}) {
    const qp = new URLSearchParams();
    if (search) qp.set('search', search);
    if (type) qp.set('type', type);
    const res = await authService.authenticatedRequest(
      `/api/users/structure/${estructuraId}/resources?${qp.toString()}`,
      { method: 'GET' }
    );
    return handleResponse(res);
  },

  async resumeRunningAttempt(examId, horario_id) {
    const hid = Number(
      (horario_id && typeof horario_id === 'object')
        ? (horario_id.horario_id ?? horario_id.horario_examen_id ?? horario_id.id)
        : horario_id
    );
    const url = `/api/exams/${Number(examId)}/running?horario_id=${hid}`;
    const res = await authService.authenticatedRequest(url, { method: 'GET' });
    if (res.status === 404) return null;
    return handleResponse(res);
  },

  async saveExamProgress(presentacionId, { answers, lockedIds, currentIndex }) {
    return authenticatedRequest(`/api/exams/presentations/${presentacionId}/save-progress`, {
      method: 'POST',
      body: JSON.stringify({ answers, lockedIds, currentIndex }),
    });
  },

  async finishExam(presentacionId, payload) {
    return authenticatedRequest(`/api/exams/presentations/${presentacionId}/finish`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async fetchExamScheduleStudents(horarioId) {
    try {
      const response = await authenticatedRequest(
        `/api/admin/exam-schedules/${horarioId}/students`,
        {
          method: "GET",
        }
      );

      return response;
    } catch (error) {
      console.error("Error en fetchExamScheduleStudents:", error);
      throw error;
    }
  },

  async fetchAvailableStudentsForExamSchedule(horarioId, params = {}) {
    try {
      const query = new URLSearchParams(params).toString();

      const response = await authenticatedRequest(
        `/api/admin/exam-schedules/${horarioId}/available-students${query ? `?${query}` : ""}`,
        {
          method: "GET",
        }
      );

      return response;
    } catch (error) {
      console.error("Error en fetchAvailableStudentsForExamSchedule:", error);
      throw error;
    }
  },

  async addStudentToExamSchedule(horarioId, estudianteId) {
    try {
      const response = await authenticatedRequest(
        `/api/admin/exam-schedules/${horarioId}/students`,
        {
          method: "POST",
          body: JSON.stringify({
            estudiante_id: Number(estudianteId),
          }),
        }
      );

      return response;
    } catch (error) {
      console.error("Error en addStudentToExamSchedule:", error);
      throw error;
    }
  },

  async removeStudentFromExamSchedule(horarioId, estudianteId) {
    try {
      const response = await authenticatedRequest(
        `/api/admin/exam-schedules/${horarioId}/students/${estudianteId}`,
        {
          method: "DELETE",
        }
      );

      return response;
    } catch (error) {
      console.error("Error en removeStudentFromExamSchedule:", error);
      throw error;
    }
  },

  async fetchProctorPresentations(params = {}) {
    const qs = new URLSearchParams(params).toString();
    const url = `/api/admin/presentations${qs ? `?${qs}` : ''}`;
    return authenticatedRequest(url);
  },

  async fetchProctoringDashboard(horarioId) {
    try {
      const response = await authenticatedRequest(
        `/api/admin/exam-schedules/${horarioId}/proctoring-dashboard`,
        {
          method: "GET",
        }
      );

      return response;
    } catch (error) {
      console.error("Error en fetchProctoringDashboard:", error);
      throw error;
    }
  },

  async createProctoringNovedad(horarioId, payload) {
    try {
      const response = await authenticatedRequest(
        `/api/admin/exam-schedules/${horarioId}/proctoring-novedades`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      return response;
    } catch (error) {
      console.error("Error en createProctoringNovedad:", error);
      throw error;
    }
  },

  async updateProctoringNovedad(horarioId, novedadId, payload) {
    try {
      const response = await authenticatedRequest(
        `/api/admin/exam-schedules/${horarioId}/proctoring-novedades/${novedadId}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        }
      );

      return response;
    } catch (error) {
      console.error("Error en updateProctoringNovedad:", error);
      throw error;
    }
  },

  async fetchProctorSnapshots(presentacionId) {
    const url = `/api/admin/presentations/${encodeURIComponent(presentacionId)}/snapshots`;
    return authenticatedRequest(url);
  },

  async fetchProctoringStudentReport(presentacionId) {
    const url = `/api/admin/presentations/${encodeURIComponent(presentacionId)}/proctoring-report`;
    return authenticatedRequest(url);
  },

  async downloadProctoringReportsZip(horarioId) {
    const url = `/api/admin/exam-schedules/${encodeURIComponent(horarioId)}/proctoring-reports/pdf-zip`;
    const res = await authService.authenticatedRequest(url, {
      method: "GET",
      headers: { Accept: "application/zip" },
    });

    if (!res.ok) {
      const raw = await res.text().catch(() => "");
      let data;
      try { data = raw ? JSON.parse(raw) : undefined; } catch (_) { }
      const msg = data?.message || data?.error || data?.details || raw || "";
      throw new Error(`No se pudo descargar el ZIP. ${res.status}${msg ? ` - ${msg}` : ""}`);
    }

    return res.blob();
  },

  async reviewProctorPresentation(presentacionId, body) {
    return authenticatedRequest(
      `/api/admin/presentations/${encodeURIComponent(presentacionId)}/review`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );
  },

  async fetchProctorStats() {
    return authenticatedRequest(`/api/admin/stats`);
  },

  async getLevelTestStatus(nivelId) {
    return authenticatedRequest(`/api/courses/levels/${nivelId}/test-status`);
  },

  async getLevelTestReview(nivelId) {
    return authenticatedRequest(`/api/courses/levels/${nivelId}/test-review`);
  },

  async getPresentationQuestions(presentacionId) {
    return authenticatedRequest(`/api/exams/tests/presentations/${presentacionId}/questions`);
  },

  async finishPresentation(presentacionId, respuestasPayloadArray, regrade = false) {
    const body = {
      respuestas: respuestasPayloadArray,
      regrade: regrade  // 👈 nuevo parámetro opcional
    };
    return authenticatedRequest(`/api/exams/tests/presentations/${presentacionId}/finish`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async savePresentationProgress(presentacionId, respuestasPayloadArray, indice) {
    const body = { respuestas: respuestasPayloadArray, indice };
    return authenticatedRequest(`/api/exams/tests/presentations/${presentacionId}/save`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  async startLevelTest(testId) {
    return authenticatedRequest(`/api/courses/tests/${testId}/presentations/start`, {
      method: 'POST',
      body: JSON.stringify({}), // siempre algo
    });
  },

  async adminListLevelsWithTests(cursoId) {
    const res = await authService.authenticatedRequest(`/api/admin/courses/${cursoId}/levels-with-tests`);
    if (!res.ok) throw new Error('No se pudo listar niveles');
    return res.json();
  },

  async adminCreateLevelTest(payload) {
    const res = await authService.authenticatedRequest(`/api/admin/tests`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
    if (res.status === 409) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j?.error || 'El nivel ya tiene un test');
    }
    if (!res.ok) throw new Error('No se pudo crear el test');
    return res.json();
  },

  async fetchLevelTestQuestions(testId, options = {}) {
    const withRefs = options.withRefs ? true : false;
    const r = await authService.authenticatedRequest(
      `/api/admin/tests/${testId}/questions?withRefs=${withRefs}`
    );
    if (!r.ok) throw new Error("No se pudieron cargar las preguntas");
    const data = await r.json();

    // Compatibilidad:
    // - Antes: devolvía un array simple de preguntas.
    // - Ahora (opcional): { questions:[...], refs:[...] }
    if (options.withRefs) {
      return {
        questions: Array.isArray(data) ? data : (data.questions || data.items || []),
        refs: Array.isArray(data) ? [] : (data.refs || []),
      };
    }
    // Modo legacy
    return Array.isArray(data) ? data : (data.questions || data.items || []);
  },

  async getCourseStructureAdmin(cursoId) {
    const url = `/api/cursos/${cursoId}/estructura/admin`
    return authenticatedRequest(url)
  },



  async deleteLevelTestRef(testId, refId) {
    return authService.authenticatedRequest(`/api/admin/tests/${testId}/refs/${refId}`, {
      method: 'DELETE',
    });
  },

  async addCustomLevelTestQuestion(testId, question) {
    return authenticatedRequest(`/api/admin/tests/${testId}/questions`, {
      method: 'POST',
      body: JSON.stringify(question),
    });
  },

  async updateLevelTestQuestion(testId, questionId, question) {
    return authenticatedRequest(`/api/admin/tests/${testId}/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify(question),
    });
  },

  async deleteLevelTestQuestion(testId, questionId) {
    const resp = await authService.authenticatedRequest(`/api/admin/tests/${testId}/questions/${questionId}`, {
      method: 'DELETE',
    });
    return handleResponse(resp);
  },

  async cloneLevelTestQuestion(testId, { fuente, fuente_id }) {
    return authenticatedRequest(`/api/admin/tests/${testId}/questions/clone`, {
      method: 'POST',
      body: JSON.stringify({ fuente, fuente_id }),
    });
  },

  async createLevelTestRef(testId, {
    fuente,
    modo = 'pool',          // 'pool' | 'single'
    // pool:
    estructura_id = null,
    include_subtree = true,
    cantidad = null,
    criterios = null,

    // single:
    fuente_id = null,

    orden = null,
  }) {
    return authenticatedRequest(`/api/admin/tests/${testId}/refs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fuente,
        modo,
        estructura_id,
        include_subtree,
        cantidad,
        criterios,
        fuente_id,
        orden,
      }),
    });
  },

  async adminDeleteLevelTest(id) {
    const url = `/api/admin/level-tests/${id}`;
    return authenticatedRequest(url, {
      method: 'DELETE',
    });
  },

  async adminUpdateLevelTest(id, payload) {
    const url = `/api/admin/level-tests/${id}`;
    return authenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async adminGetLevelTest(id) {
    const url = `/api/admin/level-tests/${id}`;
    return authenticatedRequest(url);
  },

  async fetchLevelTestProctoringDashboard(testId) {
    return authenticatedRequest(`/api/admin/level-tests/${encodeURIComponent(testId)}/proctoring-dashboard`);
  },

  async createLevelTestNovedad(testId, payload) {
    return authenticatedRequest(`/api/admin/level-tests/${encodeURIComponent(testId)}/proctoring-novedades`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    });
  },

  async updateLevelTestNovedad(testId, novedadId, payload) {
    return authenticatedRequest(`/api/admin/level-tests/${encodeURIComponent(testId)}/proctoring-novedades/${encodeURIComponent(novedadId)}`, {
      method: "PATCH",
      body: JSON.stringify(payload || {}),
    });
  },

  async fetchLevelTestPresentationEvidence(presentationId) {
    return authenticatedRequest(`/api/admin/level-test-presentations/${encodeURIComponent(presentationId)}/evidence`);
  },

  // ----- VER PERFIL ----

  async getMyProfileOverview(include = "") {
    const qs = include ? `?include=${encodeURIComponent(include)}` : "";
    return authenticatedRequest(`/api/users/profile/overview${qs}`);
  },

  // === REPORTES SIMPLIFICADOS (fase 1) ===
  async getReportsCatalog({ facultad_id = "" } = {}) {
    const reportCatalogQs = new URLSearchParams();
    if (facultad_id !== undefined && facultad_id !== null && String(facultad_id).trim() !== '') {
      reportCatalogQs.set('facultad_id', String(facultad_id));
    }

    try {
      const reportCatalog = await authenticatedRequest(`/api/reports/catalog${reportCatalogQs.toString() ? `?${reportCatalogQs.toString()}` : ''}`);
      if (reportCatalog && (Array.isArray(reportCatalog.faculties) || Array.isArray(reportCatalog.courses) || Array.isArray(reportCatalog.schedules))) {
        return {
          faculties: Array.isArray(reportCatalog.faculties) ? reportCatalog.faculties : [],
          courses: Array.isArray(reportCatalog.courses) ? reportCatalog.courses : [],
          users: Array.isArray(reportCatalog.users) ? reportCatalog.users : [],
          schedules: Array.isArray(reportCatalog.schedules) ? reportCatalog.schedules : [],
        };
      }
    } catch (_) {
      // Mantiene compatibilidad si el backend todavía no tiene /api/reports/catalog.
    }

    const [facultiesRes, coursesRes, usersRes] = await Promise.all([
      this.fetchFacultades({ showAll: true }).catch(() => []),
      this.fetchCourses({ page: 1, limit: 500, showAll: true, facultad_id }).catch(() => []),
      this.fetchUsers({ page: 1, limit: 1000, facultad_id }).catch(() => []),
    ]);

    const faculties = Array.isArray(facultiesRes)
      ? facultiesRes
      : (facultiesRes?.facultades || facultiesRes?.items || facultiesRes?.data || []);

    const courses = Array.isArray(coursesRes)
      ? coursesRes
      : (coursesRes?.courses || coursesRes?.items || coursesRes?.data || []);

    const users = Array.isArray(usersRes)
      ? usersRes
      : (usersRes?.users || usersRes?.items || usersRes?.data || []);

    let schedules = [];
    try {
      const schedulesRes = await this.fetchExamSchedules({ page: 1, limit: 1000, facultad_id });
      schedules = Array.isArray(schedulesRes)
        ? schedulesRes
        : (schedulesRes?.schedules || schedulesRes?.items || schedulesRes?.data || []);
    } catch (_) {
      schedules = [];
    }

    const normFaculties = (faculties || []).map((f) => ({
      id: Number(f?.id ?? f?.facultad_id),
      nombre: f?.nombre || f?.facultad_nombre || `Facultad ${f?.id ?? f?.facultad_id}`,
    })).filter((x) => Number.isFinite(x.id) && x.id > 0);

    const normCourses = (courses || []).map((c) => ({
      id: Number(c?.id ?? c?.curso_id),
      nombre: c?.nombre || c?.curso_nombre || c?.titulo || `Curso ${c?.id ?? c?.curso_id}`,
      codigo: c?.codigo || c?.codigo_curso || '',
      tipo: c?.tipo || c?.tipo_curso || '',
      facultad_id: Number(c?.facultad_id ?? c?.faculty_id) || null,
      facultad_nombre: c?.facultad_nombre || c?.facultad || '',
      activo: c?.activo ?? true,
      para_todos: !!(c?.para_todos || c?.paraTodos),
    })).filter((x) => Number.isFinite(x.id) && x.id > 0);

    const normUsers = (users || []).map((u) => {
      const role = Array.isArray(u?.roles)
        ? (u.roles[0]?.nombre || u.roles[0]?.rol || u.roles[0]?.role || u.roles[0] || '')
        : (u?.rol || u?.role || '');
      return {
        id: Number(u?.id ?? u?.usuario_id ?? u?.user_id),
        nombre: u?.nombre || [u?.nombres, u?.apellidos].filter(Boolean).join(' ').trim() || u?.displayName || `Usuario ${u?.id ?? u?.usuario_id}`,
        email: u?.email || u?.correo || '',
        identificacion: u?.cedula || u?.identificacion || '',
        rol: String(role || '').toLowerCase(),
        estado: u?.activo === false ? 'inactivo' : (u?.estado || 'activo'),
        facultad_nombre: u?.facultad_nombre || u?.facultad || '',
        facultad_id: Number(u?.facultad_id) || null,
        auth_foto_url: u?.auth_foto_url || u?.foto || u?.avatar || '',
        fecha_inscripcion: u?.fecha_inscripcion || u?.fechaInscripcion || '',
        ultimo_acceso_fecha: u?.ultimo_acceso_fecha || u?.ultimoAccesoFecha || '',
        ultimo_acceso_curso: u?.ultimo_acceso_curso || u?.ultimoAccesoCurso || '',
        ultimo_ingreso: u?.ultimo_ingreso || u?.ultimoIngreso || '',
      };
    }).filter((x) => Number.isFinite(x.id) && x.id > 0);

    const normSchedules = (schedules || []).map((s) => {
      const id = Number(s?.id ?? s?.horario_id);
      const codigo = s?.codigo || s?.codigo_horario || s?.codigo_examen || `H-${id || ''}`;
      return {
        id,
        horario_id: id,
        curso_id: Number(s?.curso_id) || null,
        facultad_id: Number(s?.facultad_id ?? s?.faculty_id) || null,
        facultad_nombre: s?.facultad_nombre || s?.facultad || '',
        curso_para_todos: !!(s?.curso_para_todos || s?.para_todos),
        codigo,
        codigo_horario: s?.codigo_horario || '',
        fecha: s?.fecha_examen || s?.fecha || '',
        modalidad: (s?.is_virtual === true || s?.is_virtual === 'true' || s?.modalidad === 'virtual') ? 'virtual' : 'presencial',
        label: `${codigo} · ${s?.fecha_examen || s?.fecha || ''} · ID ${id || ''}`,
      };
    }).filter((x) => Number.isFinite(x.id) && x.id > 0);

    return {
      faculties: normFaculties,
      courses: normCourses,
      users: normUsers,
      schedules: normSchedules,
    };
  },

  async getCourseReport(filters = {}) {
    const normalizedFilters = { ...(filters || {}), tipo_curso: 'regular' };
    delete normalizedFilters.search;
    const qs = new URLSearchParams();
    Object.entries(normalizedFilters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') qs.set(k, String(v));
    });

    try {
      return await authenticatedRequest(`/api/reports/course-report${qs.toString() ? `?${qs.toString()}` : ''}`);
    } catch (err) {
      if (err?.status && err.status !== 404) throw err;
    }

    const catalog = await this.getReportsCatalog({ facultad_id: normalizedFilters?.facultad_id || '' });
    let courses = Array.isArray(catalog?.courses) ? [...catalog.courses] : [];

    courses = courses.filter((c) => String(c.tipo || c.tipo_curso || '').toLowerCase() === 'regular');
    if (normalizedFilters?.curso_id) courses = courses.filter((c) => String(c.id) === String(normalizedFilters.curso_id));

    const ids = courses.map((c) => c.id).filter(Boolean);
    const summaryRes = ids.length ? await this.getCoursesSummary(ids).catch(() => []) : [];
    const summaryRows = Array.isArray(summaryRes) ? summaryRes : (summaryRes?.items || summaryRes?.rows || summaryRes?.data || []);

    const byId = new Map(courses.map((c) => [String(c.id), c]));
    let rows = (summaryRows || []).map((r) => {
      const cid = String(r?.curso_id ?? r?.id ?? '');
      const c = byId.get(cid) || {};
      return {
        id: r?.curso_id ?? c.id,
        curso_id: r?.curso_id ?? c.id,
        nombre: r?.nombre || c.nombre || `Curso ${cid}`,
        codigo: c.codigo || r?.codigo || r?.codigo_curso || '',
        facultad_nombre: r?.facultad_nombre || c.facultad_nombre || '',
        tipo: r?.tipo || c.tipo || '',
        inscritos_total: Number(r?.inscritos_total ?? r?.inscritos ?? 0),
        curso_aprobados: Number(r?.curso_aprobados ?? r?.aprobados ?? 0),
        curso_reprobados: Number(r?.curso_reprobados ?? r?.reprobados ?? 0),
        progreso_promedio_pct: Number(r?.progreso_promedio_pct ?? r?.progreso_pct ?? 0),
        exam_aprobacion_pct: Number(r?.exam_aprobacion_pct ?? 0),
      };
    });

    if (!rows.length) {
      rows = courses.map((c) => ({
        id: c.id,
        curso_id: c.id,
        nombre: c.nombre,
        codigo: c.codigo || '',
        facultad_nombre: c.facultad_nombre || '',
        tipo: c.tipo || '',
        inscritos_total: 0,
        curso_aprobados: 0,
        curso_reprobados: 0,
        progreso_promedio_pct: 0,
        exam_aprobacion_pct: 0,
      }));
    }

    let students = [];
    let selectedCourse = null;
    let courseConfig = null;
    let enrollmentTrend = [];

    if (normalizedFilters?.curso_id) {
      selectedCourse = courses.find((c) => String(c.id) === String(normalizedFilters.curso_id)) || null;

      const studentsRes = await this.getCourseStudentsReport(normalizedFilters.curso_id).catch(() => []);
      const studentRowsRaw = Array.isArray(studentsRes) ? studentsRes : (studentsRes?.rows || studentsRes?.items || studentsRes?.data || studentsRes?.students || []);
      students = studentRowsRaw.map((s, idx) => ({
        id: Number(s?.usuario_id ?? s?.id ?? s?.estudiante_id) || idx + 1,
        usuario_id: Number(s?.usuario_id ?? s?.id ?? s?.estudiante_id) || idx + 1,
        nombre: s?.nombre || [s?.nombres, s?.apellidos].filter(Boolean).join(' ').trim() || `Estudiante ${idx + 1}`,
        email: s?.email || s?.correo || '',
        identificacion: s?.cedula || s?.identificacion || '',
        estado: s?.estado || s?.estado_curso || '',
        fecha_inscripcion: s?.fecha_inscripcion || s?.fechaInscripcion || s?.matricula || '',
        progreso_pct: Number(s?.progreso_pct ?? s?.progreso ?? s?.porcentaje_progreso ?? 0),
        examen_estado: s?.examen_estado || s?.estado_examen || s?.resultado_examen || '',
        auth_foto_url: s?.auth_foto_url || s?.foto_url || s?.foto || s?.avatar || '',
        curso_nombre: s?.curso_nombre || selectedCourse?.nombre || '',
      }));

      const structureRes = await this.getCourseStructureAdmin(normalizedFilters.curso_id).catch(() => []);
      const structure = Array.isArray(structureRes)
        ? structureRes
        : (structureRes?.structure || structureRes?.items || structureRes?.data || []);
      if (Array.isArray(structure) && structure.length) {
        const countBy = (tipo) => structure.filter((item) => String(item?.tipo || '').toLowerCase() === tipo).length;
        courseConfig = {
          niveles: countBy('nivel'),
          modulos: countBy('modulo'),
          componentes: countBy('componente'),
          tareas: countBy('tarea'),
          preguntas: Number(structureRes?.stats?.total_preguntas ?? structureRes?.total_preguntas ?? 0),
        };
      }

      const trendMap = new Map();
      students.forEach((student) => {
        if (!student.fecha_inscripcion) return;
        const raw = String(student.fecha_inscripcion).slice(0, 10);
        const label = raw.length >= 7 ? raw.slice(0, 7) : raw;
        if (!label) return;
        trendMap.set(label, (trendMap.get(label) || 0) + 1);
      });
      enrollmentTrend = Array.from(trendMap.entries())
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        .map(([label, count]) => ({ label, count }));
    }

    const totalEnrollments = rows.reduce((a, r) => a + Number(r.inscritos_total || 0), 0);
    const totalApproved = rows.reduce((a, r) => a + Number(r.curso_aprobados || 0), 0);
    const avgProgress = rows.length ? rows.reduce((a, r) => a + Number(r.progreso_promedio_pct || 0), 0) / rows.length : 0;
    const typesMap = new Map();
    rows.forEach((r) => typesMap.set(r.tipo || 'Sin tipo', (typesMap.get(r.tipo || 'Sin tipo') || 0) + 1));

    return {
      mode: 'aggregate',
      selectedCourse,
      courseConfig,
      students,
      enrollmentTrend,
      appliedFilters: [
        normalizedFilters?.facultad_id ? { key: 'facultad', label: 'Facultad', value: (catalog.faculties.find((f) => String(f.id) === String(normalizedFilters.facultad_id))?.nombre || normalizedFilters.facultad_id) } : null,
        normalizedFilters?.curso_id ? { key: 'curso', label: 'Curso', value: (courses.find((c) => String(c.id) === String(normalizedFilters.curso_id))?.nombre || normalizedFilters.curso_id) } : null,
        normalizedFilters?.from ? { key: 'from', label: 'Desde', value: normalizedFilters.from } : null,
        normalizedFilters?.to ? { key: 'to', label: 'Hasta', value: normalizedFilters.to } : null,
      ].filter(Boolean),
      rows,
      kpis: {
        totalCourses: rows.length,
        totalEnrollments,
        totalApproved,
        avgProgress,
      },
      charts: {
        enrollmentsByCourse: rows.map((r) => ({ label: r.nombre, inscritos: r.inscritos_total })),
        types: Array.from(typesMap.entries()).map(([label, value]) => ({ label, value })),
        enrollmentTrend,
      },
    };
  },

  async getUserReportScope(usuarioId) {
    return authenticatedRequest(`/api/reports/user-filter-scope/${encodeURIComponent(usuarioId)}`);
  },

  async getUserReport(filters = {}) {
    const qs = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') qs.set(k, String(v));
    });

    try {
      return await authenticatedRequest(`/api/reports/user-report${qs.toString() ? `?${qs.toString()}` : ''}`);
    } catch (err) {
      if (err?.status && err.status !== 404) throw err;
    }

    const catalog = await this.getReportsCatalog({ facultad_id: filters?.facultad_id || '' });
    const allUsers = Array.isArray(catalog?.users) ? [...catalog.users] : [];

    if ((filters?.mode || 'general') === 'expediente' && filters?.usuario_id) {
      const userId = Number(filters.usuario_id);
      const userBase = allUsers.find((u) => Number(u.id) === userId) || await this.getUser(userId).catch(() => ({}));
      const [studentCourses, teacherRows, coins, streaks] = await Promise.all([
        this.getStudentReport(userId).catch(() => []),
        this.getTeacherReport(userId).catch(() => []),
        this.getCoinsByStudent(userId).catch(() => null),
        this.getStreaksByStudent(userId).catch(() => null),
      ]);

      const studentList = Array.isArray(studentCourses) ? studentCourses : (studentCourses?.rows || studentCourses?.items || []);
      const teacherList = Array.isArray(teacherRows) ? teacherRows : (teacherRows?.rows || teacherRows?.items || []);
      const courseRowsRaw = studentList.length ? studentList : teacherList;
      const courseRows = courseRowsRaw.map((r, idx) => ({
        id: r?.curso_id ?? r?.course_id ?? idx + 1,
        curso_nombre: r?.curso_nombre || r?.nombre || r?.course_name || 'Curso',
        facultad_nombre: r?.facultad_nombre || '',
        tipo_curso: r?.tipo_curso || r?.tipo || '',
        estado_curso: r?.estado_curso || r?.estado || '',
        progreso_pct: Number(r?.progreso_pct ?? r?.progreso ?? 0),
        examen_estado: r?.examen_estado || '',
      }));

      const attempts = studentList.flatMap((r, idx) => {
        const intento = r?.intentos || r?.attempts || [];
        if (!Array.isArray(intento)) return [];
        return intento.map((a, i) => ({
          id: `${idx}-${i}`,
          curso_nombre: r?.curso_nombre || r?.nombre || 'Curso',
          tipo: a?.tipo || a?.tipo_evaluacion || a?.nombre || 'Intento',
          fecha: a?.fecha || a?.fecha_inicio || '',
          estado: a?.estado || '',
          porcentaje: Number(a?.porcentaje ?? a?.nota ?? 0),
        }));
      });

      const avgProgress = courseRows.length ? courseRows.reduce((a, r) => a + Number(r.progreso_pct || 0), 0) / courseRows.length : 0;
      const approvedCourses = courseRows.filter((r) => String(r.examen_estado || '').toLowerCase().includes('aprob')).length;

      return {
        mode: 'expediente',
        appliedFilters: [
          { key: 'modo', label: 'Modo', value: 'Expediente específico' },
          { key: 'usuario', label: 'Usuario', value: userBase?.nombre || userBase?.email || `#${userId}` },
          coins?.saldo != null ? { key: 'coins', label: 'Coins', value: coins.saldo } : null,
          streaks?.racha_actual_dias != null ? { key: 'racha', label: 'Racha', value: `${streaks.racha_actual_dias} días` } : null,
        ].filter(Boolean),
        user: {
          id: userId,
          nombre: userBase?.nombre || [userBase?.nombres, userBase?.apellidos].filter(Boolean).join(' ').trim() || `Usuario ${userId}`,
          email: userBase?.email || userBase?.correo || '',
          identificacion: userBase?.identificacion || userBase?.cedula || '',
          rol: userBase?.rol || (Array.isArray(userBase?.roles) ? userBase.roles.join(', ') : ''),
          estado: userBase?.activo === false ? 'inactivo' : (userBase?.estado || 'activo'),
          auth_foto_url: userBase?.auth_foto_url || userBase?.foto || userBase?.avatar || '',
          fecha_inscripcion: userBase?.fecha_inscripcion || userBase?.fechaInscripcion || '',
          ultimo_acceso_fecha: userBase?.ultimo_acceso_fecha || userBase?.ultimoAccesoFecha || '',
          ultimo_acceso_curso: userBase?.ultimo_acceso_curso || userBase?.ultimoAccesoCurso || '',
          ultimo_ingreso: userBase?.ultimo_ingreso || userBase?.ultimoIngreso || '',
        },
        courses: courseRows,
        attempts,
        kpis: {
          totalCourses: courseRows.length,
          approvedCourses,
          avgProgress,
        },
        rows: courseRows,
      };
    }

    let rows = [...allUsers];
    if (filters?.rol) rows = rows.filter((u) => String(u.rol || '').toLowerCase().includes(String(filters.rol).toLowerCase()));
    if (filters?.search) {
      const term = String(filters.search).toLowerCase();
      rows = rows.filter((u) => `${u.nombre} ${u.email} ${u.identificacion}`.toLowerCase().includes(term));
    }

    const roleCount = new Map();
    rows.forEach((r) => roleCount.set(r.rol || 'sin rol', (roleCount.get(r.rol || 'sin rol') || 0) + 1));

    return {
      mode: 'general',
      appliedFilters: [
        { key: 'modo', label: 'Modo', value: 'Listado general' },
        filters?.rol ? { key: 'rol', label: 'Rol', value: filters.rol } : null,
        filters?.facultad_id ? { key: 'facultad', label: 'Facultad', value: (catalog.faculties.find((f) => String(f.id) === String(filters.facultad_id))?.nombre || filters.facultad_id) } : null,
      ].filter(Boolean),
      rows: rows.map((u) => ({
        ...u,
        curso_nombre: u.curso_nombre || u.ultimo_acceso_curso || '',
      })),
      kpis: {
        totalUsers: rows.length,
        activeUsers: rows.filter((u) => String(u.estado || '').toLowerCase() !== 'inactivo').length,
        students: rows.filter((u) => String(u.rol || '').includes('estudiante')).length,
        teachersAndStaff: rows.filter((u) => !String(u.rol || '').includes('estudiante')).length,
      },
      charts: {
        roles: Array.from(roleCount.entries()).map(([label, value]) => ({ label, value })),
        byCourse: [],
      },
    };
  },

  async getExamReport(filters = {}) {
    const qs = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') qs.set(k, String(v));
    });

    try {
      return await authenticatedRequest(`/api/reports/exam-report${qs.toString() ? `?${qs.toString()}` : ''}`);
    } catch (err) {
      if (err?.code === 'HORARIO_NOT_FOUND') throw err;
      if (err?.status && err.status !== 404) throw err;
    }

    const catalog = await this.getReportsCatalog({ facultad_id: filters?.facultad_id || '' });
    const coursesById = new Map((catalog.courses || []).map((c) => [String(c.id), c]));

    const params = { page: 1, limit: 1000 };
    if (filters?.facultad_id) params.facultad_id = filters.facultad_id;
    if (filters?.curso_id) params.curso_id = filters.curso_id;
    if (filters?.from) params.fecha_desde = filters.from;
    if (filters?.to) params.fecha_hasta = filters.to;
    if (filters?.modalidad === 'virtual') params.is_virtual = true;
    if (filters?.modalidad === 'presencial') params.is_virtual = false;

    const schedulesRes = await this.fetchExamSchedules(params).catch(() => ({ schedules: [] }));
    let rows = Array.isArray(schedulesRes) ? schedulesRes : (schedulesRes?.schedules || schedulesRes?.items || schedulesRes?.data || []);

    rows = rows.map((r) => {
      const cid = String(r?.curso_id || '');
      const c = coursesById.get(cid) || {};
      const capacidad_total = Number(r?.capacidad_total ?? r?.cupos_totales ?? r?.capacidad ?? ((r?.cupos_disponibles || 0) + (r?.cupos_ocupados || 0)) ?? 0);
      const agendados = Number(r?.agendados ?? r?.cupos_ocupados ?? 0);
      const presentados = Number(r?.presentados ?? 0);
      const aprobados = Number(r?.aprobados ?? 0);
      const reprobados = Number(r?.reprobados ?? 0);
      const ausentes = Number(r?.ausentes ?? 0);
      const invalidados = Number(r?.invalidados ?? 0);
      const fechaBase = r?.fecha_examen || r?.fecha || '';
      const horaInicio = r?.hora_inicio || '';
      const horaFin = r?.hora_fin || '';
      return {
        id: Number(r?.id ?? r?.horario_id),
        codigo: r?.codigo || r?.codigo_examen || r?.codigo_horario || r?.codigo_acceso || `H-${r?.id ?? r?.horario_id}`,
        codigo_horario: r?.codigo_horario || r?.codigo || '',
        curso_id: Number(r?.curso_id) || null,
        curso_nombre: r?.curso_nombre || c.nombre || 'Curso',
        fecha: fechaBase,
        fecha_hora: [fechaBase, horaInicio].filter(Boolean).join(' '),
        hora_inicio: horaInicio,
        hora_fin: horaFin,
        modalidad: (r?.is_virtual === true || r?.is_virtual === 'true' || String(r?.modalidad || '').toLowerCase() === 'virtual') ? 'virtual' : 'presencial',
        capacidad_total,
        agendados,
        presentados,
        aprobados,
        reprobados,
        ausentes,
        invalidados,
        tiempoConfigurado: Number(r?.tiempo_limite_minutos ?? r?.tiempo_configurado ?? r?.duracion_minutos ?? 0),
        numero_preguntas: Number(r?.numero_preguntas ?? 0),
        requiere_codigo: !!r?.requiere_codigo,
        habilitacion_unitaria_estudiantes: !!r?.habilitacion_unitaria_estudiantes,
        requiere_camara_microfono: !!r?.requiere_camara_microfono,
        camara_nivel: r?.camara_nivel || 'no_requerido',
        microfono_nivel: r?.microfono_nivel || 'no_requerido',
        numero_fotos_requeridas: Number(r?.numero_fotos_requeridas ?? 0),
        umbral_decibeles_microfono: r?.umbral_decibeles_microfono ?? null,
        cierre_por_penalizaciones: !!r?.cierre_por_penalizaciones,
        limite_penalizaciones: Number(r?.limite_penalizaciones ?? 0),
        permitir_navegacion: !!r?.permitir_navegacion,
        mostrar_resumen_final: !!r?.mostrar_resumen_final,
        porcentaje_aprobacion: Number(r?.porcentaje_aprobacion ?? 0),
        nota_maxima: Number(r?.nota_maxima ?? 0),
        responsables: Array.isArray(r?.responsables) ? r.responsables : [],
        notas: r?.notas || null,
        tiempo: r?.tiempo || null,
      };
    }).filter((r) => Number.isFinite(r.id));

    if (filters?.horario_id) rows = rows.filter((r) => String(r.id) === String(filters.horario_id));
    if (filters?.search) {
      const term = String(filters.search).toLowerCase();
      rows = rows.filter((r) => `${r.codigo} ${r.curso_nombre}`.toLowerCase().includes(term));
    }

    const totalSchedules = rows.length;
    const scheduled = rows.reduce((a, r) => a + r.agendados, 0);
    const approved = rows.reduce((a, r) => a + r.aprobados, 0);
    const presented = rows.reduce((a, r) => a + r.presentados, 0);
    const capacity = rows.reduce((a, r) => a + r.capacidad_total, 0);
    const attendancePct = scheduled > 0 ? (presented / scheduled) * 100 : 0;
    const occupancyPct = capacity > 0 ? (scheduled / capacity) * 100 : 0;

    let singleSchedule = null;
    if (rows.length === 1) {
      const schedule = rows[0];
      const studentRes = await this.fetchExamStudents(schedule.id).catch(() => []);
      const students = Array.isArray(studentRes) ? studentRes : (studentRes?.students || studentRes?.items || studentRes?.data || []);
      singleSchedule = {
        schedule,
        responsables: schedule.responsables || [],
        config: schedule,
        students: students.map((s, idx) => {
          const estado = s?.estado
            || (s?.aprobado === true ? 'Aprobado' : (s?.aprobado === false ? 'Reprobado' : 'Pendiente'));
          return {
            id: s?.id ?? s?.usuario_id ?? s?.estudiante_id ?? idx + 1,
            usuario_id: s?.usuario_id ?? null,
            nombre: s?.nombre || [s?.nombres, s?.apellidos].filter(Boolean).join(' ').trim() || 'Estudiante',
            email: s?.email || s?.correo || '',
            identificacion: s?.codigo_institucional || s?.identificacion || '',
            estado,
            habilitado: s?.habilitado ?? s?.enabled ?? false,
            porcentaje: Number(s?.porcentaje_obtenido ?? s?.nota ?? 0),
            modalidad: s?.is_virtual === true ? 'Virtual' : (s?.modalidad || schedule.modalidad || ''),
            horario: schedule.fecha_hora || schedule.fecha || '',
            codigo: schedule.codigo || '',
            auth_foto_url: s?.auth_foto_url || s?.foto || '',
            proctor_estado: s?.proctor_estado || '',
            fotos: Number(s?.fotos ?? 0),
            codigo_usado: s?.codigo_usado || '',
            tiempo_empleado_segundos: s?.tiempo_empleado_segundos ?? null,
            puntuacion_obtenida: s?.puntuacion_obtenida ?? null,
            puntuacion_maxima: s?.puntuacion_maxima ?? null,
            proctor_motivo: s?.proctor_motivo || '',
            revisor_nombre: s?.revisor_nombre || '',
            proctor_fecha_revision: s?.proctor_fecha_revision || null,
            proctor_fotos: Array.isArray(s?.proctor_fotos) ? s.proctor_fotos : [],
            proctor_metrics: s?.proctor_metrics || null,
          };
        }),
      };
    }

    return {
      appliedFilters: [
        filters?.facultad_id ? { key: 'facultad', label: 'Facultad', value: (catalog.faculties.find((f) => String(f.id) === String(filters.facultad_id))?.nombre || filters.facultad_id) } : null,
        filters?.curso_id ? { key: 'curso', label: 'Curso', value: (catalog.courses.find((c) => String(c.id) === String(filters.curso_id))?.nombre || filters.curso_id) } : null,
        filters?.modalidad ? { key: 'modalidad', label: 'Modalidad', value: filters.modalidad } : null,
        filters?.from ? { key: 'from', label: 'Desde', value: filters.from } : null,
        filters?.to ? { key: 'to', label: 'Hasta', value: filters.to } : null,
      ].filter(Boolean),
      rows,
      singleSchedule,
      kpis: {
        totalSchedules,
        scheduled,
        approved,
        attendancePct,
        occupancyPct,
      },
      charts: {
        bySchedule: rows.map((r) => ({ label: r.codigo, capacidad: r.capacidad_total, agendados: r.agendados })),
        results: [
          { label: 'Aprobados', value: rows.reduce((a, r) => a + r.aprobados, 0) },
          { label: 'Reprobados', value: rows.reduce((a, r) => a + r.reprobados, 0) },
          { label: 'Ausentes', value: rows.reduce((a, r) => a + r.ausentes, 0) },
          { label: 'Invalidados', value: rows.reduce((a, r) => a + r.invalidados, 0) },
        ].filter((x) => x.value > 0),
      },
    };
  },


  // === REPORTES  ===
  async getOverview() {
    return authenticatedRequest(`/api/reports/overview`);
  },
  async getCoursesSummary(courseIds = []) {
    const qs = courseIds.map(id => 'curso_id=' + encodeURIComponent(id)).join('&');
    return authenticatedRequest(`/api/reports/courses${qs ? `?${qs}` : ''}`);
  },
  async getCourseStudentsReport(cursoId) {
    return authenticatedRequest(`/api/reports/course/${cursoId}/students`);
  },
  async getLevelTestTakers(testId) {
    return authenticatedRequest(`/api/reports/level-test/${encodeURIComponent(testId)}/takers`);
  },
  async getLevelTestReportPreview(testId, usuarioId) {
    return authenticatedRequest(`/api/reports/level-test/${encodeURIComponent(testId)}/student/${encodeURIComponent(usuarioId)}/preview`);
  },
  async downloadLevelTestReportPdf(testId, usuarioId) {
    const url = `/api/reports/level-test/${encodeURIComponent(testId)}/student/${encodeURIComponent(usuarioId)}/pdf`;
    const res = await authService.authenticatedRequest(url, {
      method: 'GET',
      headers: { Accept: 'application/pdf' },
    });

    if (!res.ok) {
      // Reusa el mismo estilo de errores del resto del service
      const raw = await res.text().catch(() => '');
      let data;
      try { data = raw ? JSON.parse(raw) : undefined; } catch (_) { /* no-op */ }
      const msg = data?.message || data?.error || data?.details || raw || '';
      throw new Error(`No se pudo descargar el PDF. ${res.status}${msg ? ` - ${msg}` : ''}`);
    }

    return res.blob();
  },
  async downloadLevelTestReportsZip(testId) {
    const url = `/api/reports/level-test/${encodeURIComponent(testId)}/takers/pdf-zip`;
    const res = await authService.authenticatedRequest(url, {
      method: 'GET',
      headers: { Accept: 'application/zip' },
    });

    if (!res.ok) {
      const raw = await res.text().catch(() => '');
      let data;
      try { data = raw ? JSON.parse(raw) : undefined; } catch (_) { }
      const msg = data?.message || data?.error || data?.details || raw || '';
      throw new Error(`No se pudo descargar el ZIP. ${res.status}${msg ? ` - ${msg}` : ''}`);
    }

    return res.blob();
  },
  async getStudentReport(usuarioId) {
    return authenticatedRequest(`/api/reports/students/${usuarioId}`);
  },
  async getTeacherReport(usuarioId) {
    return authenticatedRequest(`/api/reports/teachers/${usuarioId}`);
  },
  async getCoinsByCourse(cursoId) {
    return authenticatedRequest(`/api/reports/coins/course/${encodeURIComponent(cursoId)}`);
  },
  async getCoinsByStudent(usuarioId) {
    return authenticatedRequest(`/api/reports/coins/student/${encodeURIComponent(usuarioId)}`);
  },
  async getStreaksByCourse(cursoId) {
    return authenticatedRequest(`/api/reports/streaks/course/${encodeURIComponent(cursoId)}`);
  },
  async getStreaksByStudent(usuarioId) {
    return authenticatedRequest(`/api/reports/streaks/student/${encodeURIComponent(usuarioId)}`);
  },
  async getExamSchedulesReport({ curso_id = '', from = '', to = '' } = {}) {
    const qs = new URLSearchParams();
    if (curso_id) qs.set('curso_id', String(curso_id));
    if (from) qs.set('from', String(from));
    if (to) qs.set('to', String(to));
    const url = `/api/reports/exams/schedules${qs.toString() ? `?${qs.toString()}` : ''}`;
    return authenticatedRequest(url);
  },
  async getExamCodes({ curso_id = '', from = '', to = '' } = {}) {
    const qs = new URLSearchParams();
    if (curso_id) qs.set('curso_id', String(curso_id));
    if (from) qs.set('from', String(from));
    if (to) qs.set('to', String(to));
    const url = `/api/reports/exams/codes${qs.toString() ? `?${qs.toString()}` : ''}`;
    return authenticatedRequest(url);
  },
  async getFunnelsCourses({ curso_id = '', from = '', to = '' } = {}) {
    const qs = new URLSearchParams();
    if (curso_id) qs.set('curso_id', String(curso_id));
    if (from) qs.set('from', String(from));
    if (to) qs.set('to', String(to));
    const url = `/api/reports/funnels/courses${qs.toString() ? `?${qs.toString()}` : ''}`;
    return authenticatedRequest(url);
  },
  async getProgressMap(cursoId) {
    return authenticatedRequest(`/api/reports/progressmap/course/${encodeURIComponent(cursoId)}`);
  },
  async getBottlenecks(cursoId, { limit = 25 } = {}) {
    const qs = new URLSearchParams({ limit: String(limit) });
    return authenticatedRequest(`/api/reports/bottlenecks/course/${encodeURIComponent(cursoId)}?${qs.toString()}`);
  },
  async getQuestionBankHealth(cursoId) {
    return authenticatedRequest(`/api/reports/questionbank/course/${encodeURIComponent(cursoId)}`);
  },
  async getBlueprintCoverage(cursoId, { test_id = '' } = {}) {
    const qs = new URLSearchParams();
    if (test_id) qs.set('test_id', String(test_id));
    const url = `/api/reports/blueprint/course/${encodeURIComponent(cursoId)}${qs.toString() ? `?${qs.toString()}` : ''}`;
    return authenticatedRequest(url);
  },
  async getWildcardsCourse(cursoId) {
    return authenticatedRequest(`/api/reports/wildcards/course/${encodeURIComponent(cursoId)}`);
  },
  async getWildcardsStudent(usuarioId) {
    return authenticatedRequest(`/api/reports/wildcards/student/${encodeURIComponent(usuarioId)}`);
  },
  async getCourseErrors(cursoId, { from = '', to = '', limit = 25 } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', String(from));
    if (to) qs.set('to', String(to));
    if (limit) qs.set('limit', String(limit));
    const url = `/api/reports/errors/course/${encodeURIComponent(cursoId)}${qs.toString() ? `?${qs}` : ''}`;
    return authenticatedRequest(url);
  },

  async getSystemActivity({ from = '', to = '' } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', String(from));
    if (to) qs.set('to', String(to));
    const url = `/api/reports/activity/system${qs.toString() ? `?${qs}` : ''}`;
    return authenticatedRequest(url);
  },

  async getUserActivity(usuarioId, { from = '', to = '', limit = 200 } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', String(from));
    if (to) qs.set('to', String(to));
    if (limit) qs.set('limit', String(limit));
    const url = `/api/reports/activity/user/${encodeURIComponent(usuarioId)}${qs.toString() ? `?${qs}` : ''}`;
    return authenticatedRequest(url);
  },
  async getStudentCourseProgressMap(usuarioId, cursoId) {
    return authenticatedRequest(
      `/api/reports/progressmap/student/${encodeURIComponent(usuarioId)}/course/${encodeURIComponent(cursoId)}`
    );
  },


  // === HELPERS DE BÚSQUEDA RÁPIDA (redirigen a lookups nuevos) ================
  async searchStudents(term, { limit = 10 } = {}) {
    const res = await this.quickSearchStudents({ page: 1, limit, search: term }).catch(() => ({ items: [] }));
    const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
    return (items || []).map(s => ({
      id: s.id ?? s.user_id ?? s.estudiante_id,
      label:
        s.nombre ||
        ((((s.apellidos ?? '') + ' ' + (s.nombres ?? '')).trim()) || s.displayName || s.email || 'Estudiante'),
      email: s.email || s.correo || '',
      // ✅ CLAVE: pasar la foto
      auth_foto_url: s.auth_foto_url || null,
      auth_foto_updated_at: s.auth_foto_updated_at || null,
    }));
  },


  async searchTeachers(term, { limit = 10 } = {}) {
    const res = await this.quickSearchTeachers({ page: 1, limit, search: term }).catch(() => ({ items: [] }));
    const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
    return (items || []).map(t => ({
      id: t.id ?? t.user_id ?? t.docente_id,
      label: t.nombre ||
        ((((t.apellidos ?? '') + ' ' + (t.nombres ?? '')).trim()) || t.displayName || 'Docente'),
      email: t.email || t.correo || ''
    }));
  },


  // Cursos a los que está inscrito un estudiante (normalizado)
  async getStudentEnrolledCourses(studentId) {
    const res = await this.getStudentCourses(studentId, { page: 1, limit: 200 }).catch(() => ({ items: [] }));
    const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
    return (items || []).map(c => ({
      id: c.curso_id ?? c.id ?? c.courseId,
      codigo_curso: c.codigo_curso ?? c.codigo ?? c.code ?? '',
      nombre: c.curso_nombre ?? c.nombre ?? c.title ?? 'Curso'
    }));
  },

  // === NUEVOS LOOKUPS RÁPIDOS (no rompen lo demás) ============================
  async quickSearchStudents({ page = 1, limit = 10, search = '' } = {}) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', search);
    return authenticatedRequest(`/api/admin/lookups/students?${params.toString()}`);
  },

  async quickSearchTeachers({ page = 1, limit = 10, search = '' } = {}) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', search);
    return authenticatedRequest(`/api/admin/lookups/teachers?${params.toString()}`);
  },

  async getBankQuestions(cursoId, bankId, { page = 1, limit = 50, search = '', tipo = '', nivel = '', activo = '' } = {}) {
    const qs = new URLSearchParams({ page, limit, search, tipo, nivel, activo });
    const url = `/api/admin/${encodeURIComponent(cursoId)}/banks/${encodeURIComponent(bankId)}/questions?${qs.toString()}`;
    const res = await authService.authenticatedRequest(url, { method: "GET" });
    return handleResponse(res); // espera { items|questions, pagination }
  },

  async createBankQuestion(cursoId, bankId, data = {}) {
    const {
      media_file,
      media_type,
      media_alt,
      media_caption,
      files,
      _temporary_files,
      _signal,
      ...payload
    } = data;
    const url = `/api/admin/${encodeURIComponent(cursoId)}/banks/${encodeURIComponent(bankId)}/questions`;

    try {
      const tempFiles = Array.isArray(files) ? files.filter(Boolean) : [];
      const shouldUseMultipartForIA = !!payload.ia && tempFiles.length > 0 && typeof window !== "undefined";

      if (shouldUseMultipartForIA) {
        const form = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (typeof value === "object") {
            form.append(key, JSON.stringify(value));
          } else {
            form.append(key, String(value));
          }
        });
        tempFiles.forEach((file) => {
          form.append("files", file, file.name || "archivo");
        });

        const res = await authService.authenticatedRequest(url, {
          method: "POST",
          body: form,
          dedupe: false,
          signal: _signal,
        });
        return handleResponse(res);
      }

      if (media_file && typeof window !== "undefined") {
        const form = new FormData();
        form.append("pregunta", payload.pregunta || "");
        form.append("tipo_pregunta", payload.tipo_pregunta || "respuesta_corta");
        form.append("opciones", JSON.stringify(payload.opciones || {}));
        form.append("respuesta_correcta", JSON.stringify(payload.respuesta_correcta || {}));
        form.append("explicacion", payload.explicacion ?? "");
        form.append("nivel_dificultad", payload.nivel_dificultad || "medio");
        form.append("puntos", String(payload.puntos ?? 1));
        form.append("tiempo_limite_segundos", payload.tiempo_limite_segundos != null ? String(payload.tiempo_limite_segundos) : "");
        form.append("activo", String(payload.activo ?? true));
        if (media_type) form.append("media_type", media_type);
        if (media_alt != null) form.append("media_alt", media_alt);
        if (media_caption != null) form.append("media_caption", media_caption);
        form.append("media_file", media_file, media_file.name);

        const res = await authService.authenticatedRequest(url, { method: "POST", body: form, signal: _signal });
        return handleResponse(res);
      }

      const res = await authService.authenticatedRequest(url, {
        method: "POST",
        body: JSON.stringify(payload),
        signal: _signal,
      });
      return handleResponse(res);
    } catch (error) {
      console.error("Error en createBankQuestion:", error);
      throw error;
    }
  },
  // Banco de preguntas por curso (usa tus enums del backend)
  async getCourseBankQuestions(cursoId, { page = 1, limit = 50, search = '', tipo = '', nivel = '', activo = '' } = {}) {
    const qs = new URLSearchParams({ page, limit, search, tipo, nivel, activo });
    return authenticatedRequest(`/api/admin/${cursoId}/bank/questions?${qs.toString()}`);
  },
  async updateBankQuestion(cursoId, bankId, questionId, data = {}) {
    const {
      media_file, media_type, media_alt, media_caption, remove_media, ...payload
    } = data;
    const url = `/api/admin/${encodeURIComponent(cursoId)}/banks/${encodeURIComponent(bankId)}/questions/${encodeURIComponent(questionId)}`;

    if (media_file && typeof window !== "undefined") {
      const form = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === "opciones" || k === "respuesta_correcta") form.append(k, JSON.stringify(v));
        else form.append(k, String(v));
      });
      if (media_type) form.append("media_type", media_type);
      if (media_alt != null) form.append("media_alt", media_alt);
      if (media_caption != null) form.append("media_caption", media_caption);
      if (remove_media != null) form.append("remove_media", String(remove_media));
      form.append("media_file", media_file, media_file.name);

      const res = await authService.authenticatedRequest(url, { method: "PUT", body: form });
      return handleResponse(res);
    }

    const res = await authService.authenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify({ ...payload, remove_media }),
    });
    return handleResponse(res);
  },

  async deleteBankQuestion(cursoId, bankId, questionId) {
    const url = `/api/admin/${encodeURIComponent(cursoId)}/banks/${encodeURIComponent(bankId)}/questions/${encodeURIComponent(questionId)}`;
    const res = await authService.authenticatedRequest(url, { method: 'DELETE' });
    return handleResponse(res);
  },

  async getCourseBanks(cursoId, { include_counts = true, activo = '' } = {}) {
    const qs = new URLSearchParams();
    if (include_counts) qs.set('include_counts', 'true');
    if (activo !== '') qs.set('activo', String(activo));
    const url = `/api/admin/${encodeURIComponent(cursoId)}/banks?${qs.toString()}`;
    const res = await authService.authenticatedRequest(url, { method: 'GET' });
    return handleResponse(res); // ← importante
  },

  async createCourseBank(cursoId, data) {
    const url = `/api/admin/${encodeURIComponent(cursoId)}/banks`;
    const res = await authService.authenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  async updateCourseBank(cursoId, bankId, data) {
    const url = `/api/admin/${encodeURIComponent(cursoId)}/banks/${encodeURIComponent(bankId)}`;
    const res = await authService.authenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  async deleteCourseBank(cursoId, bankId) {
    const url = `/api/admin/${encodeURIComponent(cursoId)}/banks/${encodeURIComponent(bankId)}`;
    const res = await authService.authenticatedRequest(url, { method: 'DELETE' });
    return handleResponse(res);
  },

  // ===== TIENDA =====
  async getStoreItems({ page = 1, limit = 20, search = '', category = '', sort = 'popular', onlyActive = true } = {}) {
    const qs = new URLSearchParams({
      page, limit,
      ...(search && { search }),
      ...(category && { category }),
      ...(sort && { sort }),
      ...(onlyActive ? { activo: '1' } : {})
    });
    return authenticatedRequest(`/api/store/items?${qs.toString()}`);
  },

  async getStoreItem(itemId) {
    return authenticatedRequest(`/api/store/items/${encodeURIComponent(itemId)}`);
  },

  // (opcional pero útil) Previsualiza la compra: total, impuestos, si alcanza saldo, etc.
  async previewStorePurchase({ item_id, quantity = 1 }) {
    return authenticatedRequest(`/api/store/checkout/preview`, {
      method: 'POST',
      body: JSON.stringify({ item_id: Number(item_id), quantity: Number(quantity) })
    });
  },

  // Comprar con UTMCoins (el backend descuenta el saldo)
  async buyStoreItem({ item_id, quantity = 1 }) {
    return authenticatedRequest(`/api/store/purchase`, {
      method: 'POST',
      body: JSON.stringify({ item_id: Number(item_id), quantity: Number(quantity) })
    });
  },

  // Mis compras (historial)
  async getMyStorePurchases({ page = 1, limit = 20, status = '' } = {}) {
    const qs = new URLSearchParams({
      page, limit,
      ...(status && { status })
    });
    return authenticatedRequest(`/api/store/me/purchases?${qs.toString()}`);
  },

  // (opcional) detalle de una compra
  async getStorePurchase(purchaseId) {
    return authenticatedRequest(`/api/store/me/purchases/${encodeURIComponent(purchaseId)}`);
  },

  // (opcional) canje de códigos / cupones de la tienda
  async redeemStoreCode(code) {
    return authenticatedRequest(`/api/store/redeem`, {
      method: 'POST',
      body: JSON.stringify({ code: String(code || '').trim() })
    });
  },

  // === ADMIN TIENDA ===
  async adminListStoreItems({ page = 1, limit = 20, search = '', category = '', sort = 'popular', activo = '' }) {
    const params = new URLSearchParams();
    params.set('page', page); params.set('limit', limit);
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (sort) params.set('sort', sort);
    if (activo !== '') params.set('activo', String(activo));
    return authenticatedRequest(`/api/store/admin/items?${params.toString()}`, { method: 'GET' });
  },

  async adminCreateStoreItem(payload) {
    return authenticatedRequest(`/api/store/admin/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  },

  async adminUpdateStoreItem(itemId, payload) {
    return authenticatedRequest(`/api/store/admin/items/${itemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  },

  async adminDeleteStoreItem(itemId) {
    return authenticatedRequest(`/api/store/admin/items/${itemId}`, { method: 'DELETE' });
  },

  async adminRestockItem(itemId, { delta }) {
    return authenticatedRequest(`/api/store/admin/items/${itemId}/restock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ delta })
    });
  },

  async adminToggleItemActive(itemId, active) {
    return authenticatedRequest(`/api/store/admin/items/${itemId}/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active })
    });
  },

  // Inicializar comodines si no existen
  async initComodinIfAbsent(componenteId) {
    return authenticatedRequest(
      `/api/courses/componentes/${encodeURIComponent(componenteId)}/comodines/init`,
      {
        method: 'POST',
        body: JSON.stringify({}) // podemos mandar vacío; el backend usa default=3
      }
    );
  },


  async getComodinesState(target) {
    const params = new URLSearchParams();

    if (target && typeof target === 'object') {
      const hasTarea = target.tareaId != null;
      const hasComp = target.componenteId != null;

      if (!hasTarea && !hasComp) {
        throw new Error('getComodinesState: Debes enviar { tareaId } o { componenteId }');
      }
      if (hasTarea) params.set('tarea_id', String(Number(target.tareaId)));
      if (hasComp) params.set('componente_id', String(Number(target.componenteId)));
    } else if (target != null) {
      // Compatibilidad antigua: número suelto = tareaId
      params.set('tarea_id', String(Number(target)));
    } else {
      throw new Error('getComodinesState: target requerido');
    }

    return authenticatedRequest(
      `/api/gamification/comodines/state?${params.toString()}`,
      { method: 'GET' }
    );
  },

  /**
   * Consumir comodín/vida/reset_vidas
   * Firma flexible:
   *   useComodin(tareaOrNull, { kind, componenteId, tareaId })
   * Prioridad:
   *   - Si llega componenteId => se manda componente_id
   *   - Si no, se usa tareaId || tareaOrNull como tarea_id
   */
  async useComodin(tareaOrNull, { kind = 'reintentar', componenteId, tareaId } = {}) {
    const body = { kind };

    if (componenteId != null) {
      body.componente_id = Number(componenteId);
    } else {
      const tid = (tareaId != null) ? Number(tareaId) : Number(tareaOrNull);
      if (!Number.isFinite(tid)) {
        throw new Error('useComodin: requiere componenteId o tareaId/tareaOrNull numérico');
      }
      body.tarea_id = tid;
    }

    return authenticatedRequest(`/api/gamification/comodines/use`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
  async claimStudentStreak() {
    const resp = await authenticatedRequest(`/api/gamification/streak/claim`, {
      method: "POST",
      body: JSON.stringify({}),
      dedupe: false,
    });

    const data = (resp && typeof resp.json === "function") ? await resp.json() : resp;

    // ✅ Si authenticatedRequest devuelve Response real:
    if (resp && typeof resp.status === "number" && resp.status >= 400) {
      const e = new Error(data?.error || "Error al reclamar racha");
      e.code = data?.code || `HTTP_${resp.status}`;
      e.status = resp.status;
      throw e;
    }

    // ✅ Si authenticatedRequest ya devuelve JSON, resp.status no existirá:
    if (data && data.ok === false) {
      const e = new Error(data?.error || "Error al reclamar racha");
      e.code = data?.code || "STREAK_CLAIM_ERROR";
      throw e;
    }

    return data;
  },

  async getGamificationSummary() {
    return authenticatedRequest(`/api/gamification/summary`);
  },

  async getTimeNow() {
    return authenticatedRequest(`/api/users/time/now`);
  },

  // ========== TÉRMINOS Y CONDICIONES DE LA PLATAFORMA ==========

  /**
   * Aceptar términos y condiciones de la plataforma
   */
  async acceptPlatformTerms({ usuario_id, clientInfo = {} }) {
    const payload = {
      usuario_id: Number(usuario_id),
      ...(clientInfo.ip && { ip: clientInfo.ip }),
      ...(clientInfo.ua && { user_agent: clientInfo.ua }),
    };

    return authenticatedRequest(`/api/users/terminos/aceptar`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async fetchRegistrationTerms() {
    return authenticatedRequest(`/api/users/terminos/registro`, {
      method: 'GET',
    });
  },

  /**
   * Verificar si el usuario aceptó los términos vigentes
   */
  async checkPlatformTermsAcceptance(usuario_id) {
    return authenticatedRequest(
      `/api/users/terminos/verificar/${encodeURIComponent(usuario_id)}`,
      { method: 'GET' }
    );
  },

  // =========================
  // 👥 FACULTAD ADMINS / COORDINADORES (facultad_usuarios)
  // =========================

  // GET /api/facultades/:id/admins?includeInactive=true|false
  async getFacultyAdmins(facultadId, { includeInactive = false } = {}) {
    const id = Number(facultadId);
    if (!Number.isFinite(id) || id <= 0) throw new Error("facultadId inválido");

    const params = new URLSearchParams();
    params.set("includeInactive", includeInactive ? "true" : "false");

    return authenticatedRequest(`/api/facultades/${id}/admins?${params.toString()}`, {
      method: "GET",
    });
  },

  // POST /api/facultades/:id/admins
  // Body: { usuario_id, rol: 'admin'|'coordinador', activo?: boolean }
  async addFacultyAdmin(facultadId, { usuario_id, rol = "admin", activo = true } = {}) {
    const id = Number(facultadId);
    if (!Number.isFinite(id) || id <= 0) throw new Error("facultadId inválido");

    const uid = Number(usuario_id);
    if (!Number.isFinite(uid) || uid <= 0) throw new Error("usuario_id inválido");

    return authenticatedRequest(`/api/facultades/${id}/admins`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        usuario_id: uid,
        rol,
        activo: typeof activo === "boolean" ? activo : true,
      }),
    });
  },

  // PUT /api/facultades/admins/:relId
  async updateFacultyAdmin(relId, { rol, activo } = {}) {
    const rid = Number(relId);
    if (!Number.isFinite(rid) || rid <= 0) throw new Error("relId inválido");

    const payload = {};
    if (rol != null) payload.rol = rol;
    if (typeof activo === "boolean") payload.activo = activo;

    return authenticatedRequest(`/api/facultades/admins/${rid}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  },

  // DELETE /api/facultades/admins/:relId
  async removeFacultyAdmin(relId) {
    const rid = Number(relId);
    if (!Number.isFinite(rid) || rid <= 0) throw new Error("relId inválido");

    return authenticatedRequest(`/api/facultades/admins/${rid}`, {
      method: "DELETE",
    });
  },

  // (opcional) Lookup rápido usando tu endpoint existente /api/admin/users
  // Útil para el modal "Agregar admin a facultad"
  async searchUsersForFacultyAdmin({ search = "", page = 1, limit = 10 } = {}) {
    return this.fetchUsers({ page, limit, search, role: "", status: "" });
  },

};
